import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {  useNavigate } from "react-router-dom";
const API_BASE_DEFAULT = "https://oxar-portal-api.azurewebsites.net";
const FILES_API_PATH = "/api/SharepointFiles/GetFileForTreatment";
const TOKEN_API_PATH = "/api/ExtendedToken/GetExtendedAuth";
// Held raw and URL-encoded at request time — the encoded form is unreadable
// and easy to double-encode by accident when someone edits it.
const TOKEN_KEY_DEFAULT = "5VfyVbt4JLIN6ySs34Naugtff4SOs/Dsk8CFCLBBsro=";
const TOKEN_SCHEME = "hmacauth ";
const DICOM_PARSER_CDN = "https://cdn.jsdelivr.net/npm/dicom-parser@1.8.21/dist/dicomParser.min.js";

// Window width / level defaults. ww = the full 8-bit range and wl its centre,
// so the default pair maps every input level to itself — the image is shown
// exactly as the scanner recorded it until the sliders are moved.
const WW_DEFAULT = 255;
const WL_DEFAULT = 128;

// Transfer syntaxes we can read raw pixels from. Anything else (JPEG / JPEG
// 2000 / RLE / deflated / big-endian) still yields its measurements — we just
// can't paint the image without a full codec.
const RAW_SYNTAXES = {
  "1.2.840.10008.1.2": 1,     // implicit VR little endian
  "1.2.840.10008.1.2.1": 1    // explicit VR little endian
};

const SLOT_ORDER = { L: 0, R: 1, U: 2, X: 3 };
const SIDE_FILTERS = [
  { key: "all", label: "All" },
  { key: "L", label: "Left" },
  { key: "R", label: "Right" },
  { key: "U", label: "Uterus" }
];

// The folliculogram's size rows, used only to say which bin a measurement
// falls in. Pass your own scale through the `rowLabels` prop if the cycle
// carries a different one.
const DEFAULT_ROW_LABELS = [
  ">25", "25", "24", "23", "22", "21", "20", "19", "18", "17",
  "16", "15", "14", "13", "12", "11", "10", "9", "2-5", "Total"
];

/* ── Small helpers ───────────────────────────────────────────────────────── */

const safe = (v) => (v === null || v === undefined ? "" : String(v));
const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return safe(v);
  return String(d.getDate()).padStart(2, "0") + "-" + MONTHS[d.getMonth()] + "-" + d.getFullYear();
}

// "YYYY-MM-DD", "13-Apr-2026", … -> epoch ms, or null when unreadable.
function parseDateMs(s) {
  if (!s) return null;
  let t = Date.parse(s);
  if (!Number.isNaN(t)) return t;
  const m = /(\d{1,2})[-\s/]([A-Za-z]{3,})[-\s/](\d{2,4})/.exec(String(s));
  if (m) { t = Date.parse(m[1] + " " + m[2] + " " + m[3]); if (!Number.isNaN(t)) return t; }
  return null;
}

function normDateKey(s) {
  const t = parseDateMs(s);
  if (t == null) return "";
  const d = new Date(t);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" +
    String(d.getDate()).padStart(2, "0");
}

// DICOM DA (YYYYMMDD) -> display date.
function dicomDate(yyyymmdd) {
  const s = safe(yyyymmdd).trim();
  if (!/^\d{8}$/.test(s)) return null;
  const d = new Date(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8));
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(d.toISOString());
}

// DICOM person name ("Last^First^Middle^…") -> "First Last".
function personName(pn) {
  const parts = safe(pn).split("^").filter(Boolean);
  return parts.length > 1 ? (parts[1] + " " + parts[0]).trim() : safe(parts[0]);
}

function posNums(list) {
  return (Array.isArray(list) ? list : [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
}
const sumOf = (list) => list.reduce((a, b) => a + b, 0);

// Sizes read the way the sonographer types them: 19, not 19.0 — but a real
// half-millimetre (8.5) keeps its decimal.
function mmText(n) {
  if (!Number.isFinite(n)) return "—";
  return String(Math.round(n * 10) / 10);
}

/* ── File-type table ─────────────────────────────────────────────────────── */

const FILE_TYPES = {
  dcm: { kind: "dicom", mime: "application/dicom", icon: "🩻" },
  png: { kind: "image", mime: "image/png", icon: "🖼" },
  jpg: { kind: "image", mime: "image/jpeg", icon: "🖼" },
  jpeg: { kind: "image", mime: "image/jpeg", icon: "🖼" },
  gif: { kind: "image", mime: "image/gif", icon: "🖼" },
  bmp: { kind: "image", mime: "image/bmp", icon: "🖼" },
  webp: { kind: "image", mime: "image/webp", icon: "🖼" },
  tif: { kind: "other", mime: "image/tiff", icon: "🖼" },
  tiff: { kind: "other", mime: "image/tiff", icon: "🖼" },
  svg: { kind: "other", mime: "image/svg+xml", icon: "🖼" },
  pdf: { kind: "pdf", mime: "application/pdf", icon: "📕" },
  txt: { kind: "text", mime: "text/plain", icon: "📄" },
  csv: { kind: "text", mime: "text/csv", icon: "📊" },
  json: { kind: "text", mime: "application/json", icon: "📄" },
  xml: { kind: "text", mime: "application/xml", icon: "📄" },
  rtf: { kind: "other", mime: "application/rtf", icon: "📝" },
  doc: { kind: "office", mime: "application/msword", icon: "📝" },
  docx: { kind: "office", mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", icon: "📝" },
  xls: { kind: "office", mime: "application/vnd.ms-excel", icon: "📊" },
  xlsx: { kind: "office", mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", icon: "📊" },
  ppt: { kind: "office", mime: "application/vnd.ms-powerpoint", icon: "📽" },
  pptx: { kind: "office", mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation", icon: "📽" },
  mp4: { kind: "video", mime: "video/mp4", icon: "🎞" },
  webm: { kind: "video", mime: "video/webm", icon: "🎞" },
  mov: { kind: "video", mime: "video/quicktime", icon: "🎞" },
  mp3: { kind: "audio", mime: "audio/mpeg", icon: "🔊" },
  wav: { kind: "audio", mime: "audio/wav", icon: "🔊" },
  zip: { kind: "archive", mime: "application/zip", icon: "🗜" }
};
const FILE_TYPE_ANY = { kind: "other", mime: "application/octet-stream", icon: "📎" };

function fileExt(name) {
  const m = /\.([A-Za-z0-9]+)$/.exec(safe(name).trim());
  return m ? m[1].toLowerCase() : "";
}
const fileType = (name) => FILE_TYPES[fileExt(name)] || FILE_TYPE_ANY;
const isDicomName = (name) => fileExt(name) === "dcm";

// What a base64 payload weighs once decoded — 3 bytes per 4 characters, less
// the padding. Worked out from the string so nothing has to be decoded twice.
function b64Size(b64) {
  const s = safe(b64).replace(/\s+/g, "");
  if (!s) return 0;
  const pad = s.slice(-2) === "==" ? 2 : s.slice(-1) === "=" ? 1 : 0;
  return Math.max(0, Math.floor((s.length * 3) / 4) - pad);
}
function fileSizeText(n) {
  if (!(n > 0)) return "";
  if (n < 1024) return n + " B";
  if (n < 1048576) return (n / 1024).toFixed(n < 10240 ? 1 : 0) + " KB";
  return (n / 1048576).toFixed(n < 10485760 ? 1 : 0) + " MB";
}

// The API writes "0001-01-01T00:00:00" where it has no date, which must not be
// shown as if it were one.
function apiFileDate(iso) {
  const s = safe(iso).trim();
  if (!s || s.indexOf("0001-01-01") === 0) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : formatDate(d.toISOString());
}

function base64ToBytes(b64) {
  const bin = atob(safe(b64).replace(/\s+/g, ""));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// SharePoint folder and file names carry spaces and brackets, so the address
// has to be percent-encoded before it can be used as a link — while the text
// on screen stays the readable form. An already-encoded address is decoded
// first so it can't be encoded twice.
function hrefFor(url) {
  const u = safe(url).trim();
  if (!u) return "";
  let raw = u;
  try { raw = decodeURI(u); } catch (e) { /* not decodable — encode as it came */ }
  try { return encodeURI(raw); } catch (e) { return u; }
}
function urlLastSegment(url) {
  const u = safe(url).split(/[?#]/)[0].replace(/\/+$/, "");
  const seg = u.slice(u.lastIndexOf("/") + 1);
  try { return decodeURIComponent(seg); } catch (e) { return seg; }
}
// The file's own address in SharePoint = the folder it sits in, plus its name.
function apiFileSpUrl(f) {
  const parent = safe(f && f.parentUrl).replace(/\/+$/, "");
  return parent && f && f.name ? parent + "/" + f.name : "";
}

/* ── dicom-parser, loaded once ───────────────────────────────────────────── */

let parserPromise = null;
function ensureDicomParser() {
  if (typeof window !== "undefined" && window.dicomParser) return Promise.resolve(window.dicomParser);
  if (parserPromise) return parserPromise;
  parserPromise = new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = DICOM_PARSER_CDN;
    el.async = true;
    el.onload = () => (window.dicomParser
      ? resolve(window.dicomParser)
      : reject(new Error("dicom-parser loaded but exposed nothing on window")));
    el.onerror = () => reject(new Error(
      "the DICOM parser library didn't load (it comes from a CDN) — check the network / content-security policy"));
    document.head.appendChild(el);
  });
  return parserPromise;
}

/* ── Reading what the scanner recorded ───────────────────────────────────── */

// Three sources, tried in order of how structured they are; the first that
// yields sizes wins.
function extractFollicles(dataSet, fileName) {
  // 1. Private tag (0009,1001) — structured JSON.
  try {
    const creator = dataSet.string("x00090010");
    const jsonStr = dataSet.string("x00091001");
    if (creator && jsonStr) {
      const meta = JSON.parse(jsonStr);
      const dia = posNums(meta && meta.follicle_diameters_mm);
      if (dia.length && meta.side) {
        const lead = Number(meta.leading_mm);
        const mean = Number(meta.mean_mm);
        return {
          side: safe(meta.side).toLowerCase().charAt(0) === "r" ? "R" : "L",
          dayLabel: meta.day ? String(meta.day) : null,
          day: meta.day != null ? (parseInt(String(meta.day).replace(/\D/g, ""), 10) || null) : null,
          date: dicomDate(meta.date) || dicomDate(dataSet.string("x00080020")),
          diameters: dia,
          leadingMm: Number.isFinite(lead) ? lead : Math.max.apply(null, dia),
          meanMm: Number.isFinite(mean) ? mean : sumOf(dia) / dia.length,
          source: "dicom-json"
        };
      }
    }
  } catch (e) { 
    //console.warn("[Imaging] private JSON tag not usable for", fileName, e);
   }

  // 2. Private tag (0009,1002) — "Right|12.3,9.1,7.0".
  try {
    const csv = dataSet.string("x00091002");
    if (csv && csv.indexOf("|") !== -1) {
      const bits = csv.split("|");
      const dia = posNums(safe(bits[1]).split(","));
      if (dia.length) {
        return {
          side: safe(bits[0]).trim().toLowerCase().charAt(0) === "r" ? "R" : "L",
          dayLabel: null,
          day: null,
          date: dicomDate(dataSet.string("x00080020")),
          diameters: dia,
          leadingMm: Math.max.apply(null, dia),
          meanMm: sumOf(dia) / dia.length,
          source: "dicom-csv"
        };
      }
    }
  } catch (e) { 
    //console.warn("[Imaging] private CSV tag not usable for", fileName, e);
   }

  // 3. ImageComments (0020,4000) — free text the sonographer typed.
  try {
    const txt = safe(dataSet.string("x00204000"));
    const sideMatch = txt.match(/(Right|Left)\s+Ovary/i);
    const dayMatch = txt.match(/DAY\s*(\d+)/i);
    const sizesMatch = txt.match(/Follicles?\s*\(mm\)\s*:\s*([\d.,\smm]+?)(?:\||$)/i);
    if (sideMatch && sizesMatch) {
      const dia = posNums(sizesMatch[1].split(","));
      if (dia.length) {
        return {
          side: sideMatch[1].toLowerCase().charAt(0) === "r" ? "R" : "L",
          dayLabel: dayMatch ? "DAY " + dayMatch[1] : null,
          day: dayMatch ? parseInt(dayMatch[1], 10) : null,
          date: dicomDate(dataSet.string("x00080020")),
          diameters: dia,
          leadingMm: Math.max.apply(null, dia),
          meanMm: sumOf(dia) / dia.length,
          source: "image-comments"
        };
      }
    }
  } catch (e) { 
    //console.warn("[Imaging] ImageComments not usable for", fileName, e); 
  }

  return null;
}

/* A transvaginal uterus view carries no follicle sizes; what it DOES carry is
   the endometrial thickness — the number the folliculogram's Endometrium row
   wants. Same three sources in the same order, plus the wording the machines
   actually burn in: "Endometrial thickness (ET): 4.2 mm", "Endo.Thickness
   7.5 mm", "ET 7.5 mm".

   Most specific pattern first, so a line that spells the measurement out is
   never matched by the loose \bET\b rule against some other number near it.
   Each allows a short digit-free gap between the term and the value: the GE
   report layout puts a column heading in between. */
const ET_PATTERNS = [
  /endometrial\s*thickness\s*\(\s*ET\s*\)[^0-9\r\n]{0,14}?([0-9]+(?:\.[0-9]+)?)\s*mm/i,
  /endo(?:metri(?:al|um))?\s*\.?\s*thickness\b[^0-9\r\n]{0,14}?([0-9]+(?:\.[0-9]+)?)\s*mm/i,
  /\bendometrium\b[^0-9\r\n]{0,10}?([0-9]+(?:\.[0-9]+)?)\s*mm/i,
  /\bET\s*[:=]\s*([0-9]+(?:\.[0-9]+)?)\s*mm/i,
  /\bET\b[^0-9\r\n]{0,10}?([0-9]+(?:\.[0-9]+)?)\s*mm/i
];
const ET_PATTERN_WORDS =
  /(triple[\s-]?line|tri[\s-]?laminar|homogen\w*|heterogen\w*|hyper[\s-]?echoic|hypo[\s-]?echoic|iso[\s-]?echoic)/i;
// What marks a file as the uterus view when it carries no ovary side to say so.
const UTERUS_WORDS = /uter|endometri|\bET\b/i;

function etFromText(txt) {
  const t = safe(txt);
  if (!t) return null;
  for (let i = 0; i < ET_PATTERNS.length; i++) {
    const m = t.match(ET_PATTERNS[i]);
    if (!m) continue;
    const mm = Number(m[1]);
    // Plausibility guard: an endometrium is a few millimetres, never 300 —
    // this stops a patient ID or a date that happened to sit next to the
    // letters "ET" from being read as a measurement.
    if (Number.isFinite(mm) && mm > 0 && mm <= 40) return mm;
  }
  return null;
}
function etPatternFromText(txt) {
  const m = safe(txt).match(ET_PATTERN_WORDS);
  if (!m) return "";
  const w = m[1].toLowerCase();
  // "Triple-line" and "trilaminar" are the same finding written two ways.
  if (/^tri/.test(w)) return "Triple-line";
  return m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
}

function extractUterus(dataSet, fileName) {
  const comments = safe(dataSet.string("x00204000"));
  const series = safe(dataSet.string("x0008103e"));
  const study = safe(dataSet.string("x00081030"));
  const text = [comments, series, study].filter(Boolean).join(" | ");

  let endoMm = null, pattern = "", day = null, dayLabel = null, source = "";

  // 1. Private tag (0009,1001) — structured JSON.
  try {
    const creator = dataSet.string("x00090010");
    const jsonStr = dataSet.string("x00091001");
    if (creator && jsonStr) {
      const meta = JSON.parse(jsonStr) || {};
      const raw = meta.endometrium_mm != null ? meta.endometrium_mm
        : meta.endometrial_thickness_mm != null ? meta.endometrial_thickness_mm
          : meta.endo_mm != null ? meta.endo_mm
            : meta.et_mm;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) {
        endoMm = n;
        source = "dicom-json";
        // Put an authored pattern through the same normaliser as typed text,
        // so "trilaminar" from a tag and "Triple-line" from ImageComments do
        // not show up as two different findings.
        const raw2 = safe(meta.endometrium_pattern || meta.endo_pattern || meta.pattern);
        pattern = etPatternFromText(raw2) || raw2;
      }
      if (meta.day != null) {
        dayLabel = String(meta.day);
        day = parseInt(String(meta.day).replace(/\D/g, ""), 10) || null;
      }
    }
  } catch (e) { 
    //console.warn("[Imaging] private JSON tag not usable for uterus in", fileName, e); 
  }

  // 2. Private tag (0009,1002) — "Uterus|7.5".
  if (endoMm == null) {
    try {
      const m = safe(dataSet.string("x00091002")).match(/^\s*uterus\s*\|\s*([0-9]+(?:\.[0-9]+)?)/i);
      if (m) {
        const n = Number(m[1]);
        if (Number.isFinite(n) && n > 0) { endoMm = n; source = "dicom-csv"; }
      }
    } catch (e) 
    { 
      //console.warn("[Imaging] private CSV tag not usable for uterus in", fileName, e);
     }
  }

  // 3. The text the sonographer typed or the machine burned in.
  if (endoMm == null) {
    endoMm = etFromText(comments);
    if (endoMm != null) source = "image-comments";
  }
  if (endoMm == null) {
    endoMm = etFromText(series);
    if (endoMm == null) endoMm = etFromText(study);
    if (endoMm != null) source = "series-description";
  }

  if (!pattern) pattern = etPatternFromText(text);
  if (day == null) {
    const dm = text.match(/DAY\s*(\d+)/i);
    if (dm) { day = parseInt(dm[1], 10); dayLabel = "DAY " + dm[1]; }
  }

  // A uterus view with no readable thickness is still a uterus view: it is
  // worth labelling as one so the list says "no endometrium reading" rather
  // than the misleading "no follicle sizes".
  if (endoMm == null && !UTERUS_WORDS.test(text)) return null;

  return {
    endoMm,
    endoPattern: pattern,
    date: dicomDate(dataSet.string("x00080020")),
    day,
    dayLabel,
    source: endoMm == null ? "none" : source
  };
}

/* ── Pixel decoding ──────────────────────────────────────────────────────── */

// First frame -> RGBA. Covers 8/16-bit greyscale and 8-bit RGB (both planar
// configurations), which is what uncompressed ultrasound output looks like.
function decodePixels(dataSet, bytes) {
  const rows = dataSet.uint16("x00280010");
  const cols = dataSet.uint16("x00280011");
  const el = dataSet.elements.x7fe00010;
  if (!rows || !cols || !el) throw new Error("no Rows / Columns / PixelData tags");

  const ts = safe(dataSet.string("x00020010")).trim();
  if (ts && !RAW_SYNTAXES[ts]) {
    throw new Error("the image uses transfer syntax " + ts +
      ", which this viewer can't decode. The measurements below were still read from the file.");
  }

  const bits = dataSet.uint16("x00280100") || 8;
  const spp = dataSet.uint16("x00280002") || 1;
  const planar = dataSet.uint16("x00280006") || 0;
  const px = rows * cols;
  const off = el.dataOffset;
  const avail = el.length != null ? el.length : bytes.length - off;
  const rgba = new Uint8ClampedArray(px * 4);

  if (spp >= 3) {
    if (bits !== 8) throw new Error("unsupported colour depth (" + bits + "-bit colour)");
    if (avail < px * 3) throw new Error("pixel data is shorter than Rows × Columns × 3");
    const src = bytes.subarray(off, off + px * 3);
    for (let i = 0; i < px; i++) {
      const r = planar === 1 ? src[i] : src[i * 3];
      const g = planar === 1 ? src[px + i] : src[i * 3 + 1];
      const b = planar === 1 ? src[2 * px + i] : src[i * 3 + 2];
      rgba[i * 4] = r; rgba[i * 4 + 1] = g; rgba[i * 4 + 2] = b; rgba[i * 4 + 3] = 255;
    }
    return { w: cols, h: rows, rgba };
  }

  let grey;
  if (bits <= 8) {
    if (avail < px) throw new Error("pixel data is shorter than Rows × Columns");
    grey = bytes.subarray(off, off + px);
  } else {
    if (avail < px * 2) throw new Error("pixel data is shorter than Rows × Columns × 2");
    // dataOffset isn't guaranteed to be 2-byte aligned, so copy the slice
    // rather than laying a Uint16Array view straight over the file buffer.
    const raw = new Uint16Array(bytes.buffer.slice(bytes.byteOffset + off, bytes.byteOffset + off + px * 2));
    let min = 65535, max = 0;
    for (let i = 0; i < raw.length; i++) { const v = raw[i]; if (v < min) min = v; if (v > max) max = v; }
    const range = Math.max(1, max - min);
    grey = new Uint8Array(px);
    for (let i = 0; i < px; i++) grey[i] = Math.round(((raw[i] - min) / range) * 255);
  }

  const invert = /MONOCHROME1/i.test(safe(dataSet.string("x00280004")));   // 0 = white
  for (let i = 0; i < px; i++) {
    const v = invert ? 255 - grey[i] : grey[i];
    rgba[i * 4] = v; rgba[i * 4 + 1] = v; rgba[i * 4 + 2] = v; rgba[i * 4 + 3] = 255;
  }
  return { w: cols, h: rows, rgba };
}

function buildLut(ww, wl) {
  const width = Math.max(1, ww);
  const lo = wl - width / 2;
  const lut = new Uint8ClampedArray(256);
  for (let v = 0; v < 256; v++) lut[v] = ((v - lo) / width) * 255;
  return lut;
}
const wlIsDefault = (ww, wl) => ww === WW_DEFAULT && wl === WL_DEFAULT;

// The decoded pixels are never touched — the window/level is a lookup table
// applied on the way to the canvas.
function drawTo(canvas, study, ww, wl) {
  if (!canvas || !study || !study.pixels) return;
  canvas.width = study.pixels.w;
  canvas.height = study.pixels.h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const img = ctx.createImageData(study.pixels.w, study.pixels.h);
  img.data.set(study.pixels.rgba);
  const w = ww == null ? WW_DEFAULT : ww;
  const l = wl == null ? WL_DEFAULT : wl;
  if (!wlIsDefault(w, l)) {
    const lut = buildLut(w, l);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) { d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; }
  }
  ctx.putImageData(img, 0, 0);
}

/* ── One study out of raw bytes ──────────────────────────────────────────── */

function parseBytes(parser, name, bytes) {
  const dataSet = parser.parseDicom(bytes);

  const meta = extractFollicles(dataSet, name) || {
    side: null, day: null, dayLabel: null,
    date: dicomDate(dataSet.string("x00080020")),
    diameters: [], leadingMm: null, meanMm: null, source: "none"
  };

  // Only asked for when the file named no ovary — a study that carries R or L
  // with sizes is an ovary view, whatever else its description mentions.
  const ut = meta.side ? null : extractUterus(dataSet, name);

  let pixels = null, pixelNote = "";
  try { pixels = decodePixels(dataSet, bytes); }
  catch (e) {
    pixelNote = (e && e.message) || "The image in this file could not be decoded.";
   // console.warn("[Imaging] pixel decode failed for", name, e);
  }

  return {
    fileName: name,
    // Stable identity for React keys and the per-image tick / view state.
    uid: name + "|" + safe(meta.date) + "|" + safe(meta.side),
    patientName: personName(dataSet.string("x00100010")),
    patientId: safe(dataSet.string("x00100020")),
    dob: dicomDate(dataSet.string("x00100030")),
    sex: safe(dataSet.string("x00100040")),
    accession: safe(dataSet.string("x00080050")),
    institution: safe(dataSet.string("x00080080")),
    studyDescription: safe(dataSet.string("x00081030")),
    seriesDescription: safe(dataSet.string("x0008103e")) || name,
    pixels,
    pixelNote,
    side: meta.side,
    day: meta.day != null ? meta.day : (ut ? ut.day : null),
    dayLabel: meta.dayLabel || (ut ? ut.dayLabel : null),
    date: meta.date || (ut ? ut.date : null),
    diameters: meta.diameters,
    leadingMm: meta.leadingMm,
    meanMm: meta.meanMm,
    metaSource: meta.source,
    // What this file is a picture of. Drives how the row and the measurements
    // panel treat it.
    kind: meta.side ? "ovary" : (ut ? "uterus" : "unknown"),
    endoMm: ut ? ut.endoMm : null,
    endoPattern: ut ? ut.endoPattern : "",
    endoSource: ut ? ut.source : ""
  };
}

function sourceText(s) {
  // A uterus view's number came from its own read, so it names that source.
  const src = s.kind === "uterus" ? s.endoSource : s.metaSource;
  return src === "dicom-json" ? "DICOM private tag (structured JSON)"
    : src === "dicom-csv" ? "DICOM private tag (CSV)"
      : src === "image-comments" ? "DICOM ImageComments (text)"
        : src === "series-description" ? "DICOM series / study description"
          : "not recorded";
}

/* ── The API ─────────────────────────────────────────────────────────────── */

/* The files API is HMAC-authenticated. The token it issues is
   clientId:signature:nonce:timestamp — a fresh nonce and the current time
   every call — so it is replay-protected and short-lived by design. That makes
   caching it a liability rather than a saving. Returns null (never throws) if
   the token can't be had, so the caller can decide whether to go on. */
async function fetchAuthToken(apiBase, tokenKey) {
  try {
    const resp = await fetch(
      apiBase + TOKEN_API_PATH + "?Key=" + encodeURIComponent(tokenKey),
      { method: "GET", headers: { Accept: "application/json" } }
    );
    if (!resp.ok) throw new Error("HTTP " + resp.status);
    const data = await resp.json();
    if (data && data.result === false) throw new Error(safe(data.errorMessage) || "the token API refused the key");
    const token = safe(data && data.content).trim();
    if (!token) throw new Error("the token API returned an empty token");
    return TOKEN_SCHEME + token;
  } catch (e) {
    //console.warn("[Imaging] could not get an auth token:", e);
    return null;
  }
}

/* One call to the files API, plus the judgement of what came back. The
   envelope carries its own status alongside the HTTP one and the two can
   disagree — a 200 wrapping an error payload is still an error — so both are
   checked here. Throws on failure; the caller decides how to say so. */
async function fetchCycleTree({ apiBase, tokenKey, userGuid, treatmentName }, onStep) {
  if (onStep) onStep("Signing in to the file service…");

  const token = await fetchAuthToken(apiBase, tokenKey);


  if (onStep) onStep("Loading DICOM files for this treatment cycle…");
  const url = apiBase + FILES_API_PATH +
    "?userGuid=" + encodeURIComponent(userGuid) +
    "&treatmentName=" + encodeURIComponent(treatmentName);
  // A missing token isn't fatal: the call is attempted anyway so the section
  // keeps working if auth isn't enforced yet, and a 401/403 below reports the
  // real reason if it is.
  const headers = { Accept: "application/json" };

   const token23 = sessionStorage.getItem("AuthToken")
  if (token23) headers.Authorization = 'Bearer ' + token23;
  const resp = await fetch(url, { method: "GET", headers });
  //console.log({resp})
  if (resp.status === 401 || resp.status === 403) {
    throw new Error(token
      ? "the file service rejected the access token (HTTP " + resp.status + ")"
      : "the file service needs an access token and one couldn't be obtained (HTTP " + resp.status + ")");
  }
  if (!resp.ok) throw new Error("the file API returned HTTP " + resp.status);
  const data = await resp.json();
  //console.log({data})
  const inner = data && data.statusCode;
  if ((inner != null && Number(inner) !== 200) || (data && data.result === false)) {
    throw new Error("the file API reported status " + safe(inner || "error") +
      (data && data.errorMessage ? " — " + safe(data.errorMessage) : ""));
  }
  return (data && data.content) || null;
}

/* The API answers with a folder tree:
     Document Hierarchy -> <contact>_<guid> -> <treatment name> -> files
   EVERY file in it is collected, whatever its extension — a treatment folder
   holds reports, photos and letters beside the scans, and all of them belong
   to this cycle. Only the .dcm ones go on to be parsed as studies; the rest
   are listed as files. A leaf that came back without a payload is still
   recorded: saying the file exists but has no content is more use than
   dropping it silently. */
function collectApiFiles(node, folder, out, path) {
  if (!node || typeof node !== "object") return out;
  const trail = path || [];
  if (Array.isArray(node)) {
    node.forEach((n) => collectApiFiles(n, folder, out, trail));
    return out;
  }
  const label = safe(node.label);
  const children = Array.isArray(node.children) ? node.children : null;
  // A file is anything that isn't a folder: it carries a payload, or the tree
  // simply stopped there. Only a node with children (or one that says so)
  // counts as a folder, so an empty leaf can't become a phantom folder name.
  if (node.base64 || (!children && node.type !== "folder")) {
    if (label) {
      out.push({
        name: label,
        base64: safe(node.base64),
        folder,
        // Every folder above this file, root first. The immediate folder alone
        // is not enough to place a file: a treatment folder has day subfolders
        // under it, and a scan in one of those is still this cycle's scan.
        path: trail.slice(),
        relativeUrl: safe(node.relativeUrl),
        parentUrl: safe(node.parentUrl),
        date: safe(node.date)
      });
    }
    return out;
  }
  if (children) {
    const next = label || folder;
    const nextTrail = label ? trail.concat(label) : trail;
    children.forEach((c) => collectApiFiles(c, next, out, nextTrail));
  }
  return out;
}

// Folder names are compared for scoping, so they are matched the way a person
// would read them: case- and whitespace-insensitive.
const normLabel = (s) => safe(s).replace(/\s+/g, " ").trim().toLowerCase();

/* This cycle's own folder in SharePoint. The API stamps the containing
   folder's absolute address on every node as `parentUrl`, so the one wanted
   here is the parentUrl whose LAST segment is the cycle's name. When the
   folder came back with no files in it there is no such parentUrl, so the
   treatment folder's own node is used instead. Returns "" when neither is
   there, rather than guessing at an address that may not exist. */
function findCycleFolderUrl(root, cycleName) {
  const want = normLabel(cycleName);
  if (!want) return "";
  let hit = "", fallback = "";
  (function walk(node) {
    if (hit || !node || typeof node !== "object") return;
    if (Array.isArray(node)) { node.forEach(walk); return; }
    const parent = safe(node.parentUrl).replace(/\/+$/, "");
    if (parent && normLabel(urlLastSegment(parent)) === want) { hit = parent; return; }
    if (!fallback && parent && normLabel(node.label) === want) fallback = parent + "/" + safe(node.label);
    if (Array.isArray(node.children)) node.children.forEach(walk);
  })(root);
  return hit || fallback;
}

/* Mark which files belong to the treatment we asked for. Files from elsewhere
   are MARKED, not dropped: they came back for this patient and are still worth
   listing under the folder they came from. What the mark buys is that only
   in-cycle scans are read into the studies list. */
function markApiFileScope(all, treatmentName) {
  const want = normLabel(treatmentName);
  const under = (f) => (f.path || []).some((p) => normLabel(p) === want);
  // The normal path: the treatment folder is somewhere ABOVE the file. It is
  // matched anywhere in the chain, not just as the immediate parent, because
  // the folder is usually organised into day subfolders.
  if (all.some(under)) {
    all.forEach((f) => { f.inCycle = under(f); });
    return all;
  }
  // Nothing in any chain carries the name. If everything came from ONE folder
  // then that folder IS the answer and the labels merely differ in
  // punctuation; otherwise nothing can be placed with confidence.
  const folders = {};
  all.forEach((f) => { folders[normLabel(f.folder)] = 1; });
  const names = Object.keys(folders);
  const oneFolder = names.length === 1;
  all.forEach((f) => { f.inCycle = oneFolder; });
  if (all.length && !oneFolder) {
    //console.warn("[Imaging] API returned files from several folders, none matching", treatmentName, names);
  }
  return all;
}

/* ── Ordering, grouping, searching ───────────────────────────────────────── */

function studyDateValue(s) {
  const t = parseDateMs(s && s.date);
  return t == null ? Infinity : t;
}
// Date ascending, then right ovary before left, then by name — the order a
// round is read in.
function studyOrder(a, b) {
  const da = studyDateValue(a), db = studyDateValue(b);
  if (da !== db) return da - db;
  const so = (x) => (x.side === "R" ? 0 : x.side === "L" ? 1 : 2);
  if (so(a) !== so(b)) return so(a) - so(b);
  return String(a.fileName).localeCompare(String(b.fileName));
}

// Heading text for a study's day ("Day 12"), or "" when the file carries no
// day at all. The private JSON tag stores a bare number, ImageComments a
// "DAY 12" string, so both spellings are normalised to one.
function dayHeading(s) {
  const raw = safe(s && (s.dayLabel || (s.day != null ? String(s.day) : ""))).trim();
  if (!raw) return "";
  return /^\d+$/.test(raw) ? "Day " + raw : raw.replace(/^day\s*/i, "Day ");
}

// What puts two studies on the same day. Date first; day number only as a
// fallback for undated files.
function groupKey(s) {
  if (s.date) return "d:" + (normDateKey(s.date) || safe(s.date));
  if (s.day != null) return "n:" + s.day;
  return "u:";
}

// The list is already in date order, so equal keys sit next to each other and
// one walk is enough to build the groups.
function groupStudies(list) {
  const groups = [];
  let cur = null;
  (list || []).forEach((s) => {
    const key = groupKey(s);
    if (!cur || cur.key !== key) {
      cur = { key, day: "", date: s.date || "", items: [] };
      groups.push(cur);
    }
    // One file of a day can carry the day number while another doesn't, so
    // take the first one found rather than only the group's first row.
    if (!cur.day) cur.day = dayHeading(s);
    cur.items.push(s);
  });
  return groups;
}

const slotKey = (s) => (s.side === "L" ? "L" : s.side === "R" ? "R" : s.kind === "uterus" ? "U" : "X");
function slotName(s) {
  const k = slotKey(s);
  return k === "L" ? "Left ovary" : k === "R" ? "Right ovary" : k === "U" ? "Uterus"
    : safe(s.seriesDescription) || safe(s.fileName);
}
// Row title: which ovary this scan is of. That is the one thing that
// distinguishes the rows within a day; the file name moves to the tooltip.
function studyRowTitle(s) {
  if (s.side === "R") return "Right ovary";
  if (s.side === "L") return "Left ovary";
  if (s.kind === "uterus") return "Uterus";
  return safe(s.seriesDescription) || safe(s.fileName);
}

/* One search box for both lists under it, because "is there a day 6 scan?" and
   "is the day 6 report here?" are the same question asked twice. Every word
   has to match, and each may match a different field. A term that is all
   digits is compiled to match only as a WHOLE number — never inside a longer
   one — or "day 3" would also return day 2 (dated 13-Apr). */
function searchTerms(query) {
  return safe(query).toLowerCase().split(/\s+/).filter(Boolean).map((t) => ({
    text: t,
    re: /^\d+$/.test(t) ? new RegExp("(^|[^\\d.])" + t + "([^\\d.]|$)") : null
  }));
}
function matchesTerms(haystack, terms) {
  const h = safe(haystack).toLowerCase();
  return terms.every((t) => (t.re ? t.re.test(h) : h.indexOf(t.text) !== -1));
}
function studyHaystack(s) {
  return [
    studyRowTitle(s), s.fileName, s.seriesDescription, s.studyDescription,
    s.side === "R" ? "right ovary R" : s.side === "L" ? "left ovary L" : "",
    s.kind === "uterus" ? "uterus endometrium endometrial ET U" : "",
    dayHeading(s), s.dayLabel, s.date, s.patientName, s.institution,
    (s.diameters || []).join(" "),
    s.leadingMm != null ? "lead " + s.leadingMm : "",
    s.endoMm != null ? "endo " + s.endoMm : "",
    s.endoPattern
  ].join(" ");
}
function fileHaystack(f) {
  return [f.name, f.folder, (f.path || []).join(" "), fileExt(f.name),
  apiFileDate(f.date), f.parseError ? "unreadable error" : ""].join(" ");
}

/* ── Size bins ───────────────────────────────────────────────────────────── */

// Turn a row label into the range of diameters it covers.
function parseRowLabel(label) {
  const s = safe(label).trim();
  if (!s || /^total$/i.test(s)) return null;
  let m = /^>\s*(\d+(?:\.\d+)?)$/.exec(s);
  if (m) return { kind: "gt", lo: Number(m[1]), hi: Infinity };
  m = /^<\s*(\d+(?:\.\d+)?)$/.exec(s);
  if (m) return { kind: "lt", lo: -Infinity, hi: Number(m[1]) };
  m = /^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/.exec(s);
  if (m) return { kind: "range", lo: Number(m[1]), hi: Number(m[2]) };
  m = /^(\d+(?:\.\d+)?)$/.exec(s);
  if (m) return { kind: "exact", lo: Number(m[1]), hi: Number(m[1]) };
  return null;
}

// Which row a measured diameter belongs in. `exact` is false when the scale
// has no row covering that size and we fell back to the nearest one rather
// than dropping the measurement.
function rowForMm(mm, labels) {
  if (!Number.isFinite(mm)) return null;
  const rows = [];
  (labels || []).forEach((lb, i) => {
    const p = parseRowLabel(lb);
    if (p) rows.push({ i, label: lb, p });
  });
  if (!rows.length) return null;

  const rounded = Math.round(mm);
  for (let a = 0; a < rows.length; a++) {                       // whole-mm row
    if (rows[a].p.kind === "exact" && rows[a].p.lo === rounded) {
      return { index: rows[a].i, label: rows[a].label, exact: true };
    }
  }
  for (let b = 0; b < rows.length; b++) {                       // banded row
    const p = rows[b].p;
    if (p.kind === "range" && mm >= p.lo && mm <= p.hi) {
      return { index: rows[b].i, label: rows[b].label, exact: true };
    }
  }
  for (let c = 0; c < rows.length; c++) {                       // open-ended row
    const p = rows[c].p;
    if ((p.kind === "gt" && mm > p.lo) || (p.kind === "lt" && mm < p.hi)) {
      return { index: rows[c].i, label: rows[c].label, exact: true };
    }
  }
  // Nearest row wins; ties go to the row listed first (the larger size).
  let best = null, bestD = Infinity;
  rows.forEach((e) => {
    const d = mm < e.p.lo ? e.p.lo - mm : mm > e.p.hi ? mm - e.p.hi : 0;
    if (d < bestD) { bestD = d; best = e; }
  });
  return best ? { index: best.i, label: best.label, exact: false } : null;
}

/* ── One image, one viewer ───────────────────────────────────────────────
   The canvas is laid out by CSS (object-fit: contain), so scale 1 is already
   "fit" and a transform on top of it is the whole zoom implementation. Zoom,
   pan and contrast belong to the image, not to the section, so two ovaries can
   be read at different settings side by side — which is why this state lives
   in the tile component and the tiles are keyed by uid. */
function DicomPic({ study, selected, onSelect }) {
  const picRef = useRef(null);
  const canvasRef = useRef(null);
  const view = useRef({ z: 1, x: 0, y: 0 });
  const [ww, setWw] = useState(WW_DEFAULT);
  const [wl, setWl] = useState(WL_DEFAULT);
  const [wlOpen, setWlOpen] = useState(false);
  const [pct, setPct] = useState(100);
  const slot = slotKey(study);

  const applyView = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const v = view.current;
    c.style.transform = "translate(" + v.x + "px," + v.y + "px) scale(" + v.z + ")";
    setPct(Math.round(v.z * 100));
  }, []);

  // W/L repaints pixels, so the sliders write state and the paint happens
  // here, once per committed value.
  useEffect(() => {
    drawTo(canvasRef.current, study, ww, wl);
    applyView();
  }, [study, ww, wl, applyView]);

  const setZoom = useCallback((z, cx, cy) => {
    const v = view.current;
    const nz = Math.max(0.5, Math.min(6, z));
    if (cx != null) {                       // keep the point under the cursor still
      const f = nz / v.z;
      v.x = cx - (cx - v.x) * f;
      v.y = cy - (cy - v.y) * f;
    }
    v.z = nz;
    if (nz <= 1) { v.x = 0; v.y = 0; }
    applyView();
  }, [applyView]);

  // Non-passive, so the page doesn't scroll while the image is being zoomed.
  useEffect(() => {
    const el = picRef.current;
    if (!el || !study.pixels) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      setZoom(view.current.z * (e.deltaY < 0 ? 1.14 : 1 / 1.14),
        e.clientX - r.left - r.width / 2, e.clientY - r.top - r.height / 2);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [setZoom, study.pixels]);

  const reset = () => {
    const v = view.current;
    v.z = 1; v.x = 0; v.y = 0;
    setWw(WW_DEFAULT); setWl(WL_DEFAULT);
    applyView();
  };

  const onPointerDown = (e) => {
    if (e.target.closest(".oximg-pic-bar") || e.target.closest(".oximg-wlpop")) return;
    if (view.current.z <= 1) return;            // nothing to pan while it fits
    e.preventDefault();
    const el = picRef.current;
    el.setPointerCapture(e.pointerId);
    el.classList.add("ox-grabbing");
    const sx = e.clientX, sy = e.clientY;
    const x0 = view.current.x, y0 = view.current.y;
    const move = (ev) => {
      view.current.x = x0 + (ev.clientX - sx);
      view.current.y = y0 + (ev.clientY - sy);
      applyView();
    };
    const up = () => {
      el.classList.remove("ox-grabbing");
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
  };

  const tag = (
    <span className={"oximg-pic-tag ox-" + slot}>{slotName(study).toUpperCase()}</span>
  );

  if (!study.pixels) {
    return (
      <div className={"oximg-pic" + (selected ? " ox-sel" : "")} ref={picRef} onClick={onSelect}>
        <div className="oximg-pic-none">
          {safe(study.pixelNote) || "This file carries no displayable image."}
        </div>
        {tag}
      </div>
    );
  }

  return (
    <div
      className={"oximg-pic" + (selected ? " ox-sel" : "")}
      ref={picRef}
      onPointerDown={onPointerDown}
      onClick={(e) => {
        if (e.target.closest(".oximg-pic-bar") || e.target.closest(".oximg-wlpop")) return;
        if (onSelect) onSelect();
      }}
    >
      <canvas ref={canvasRef} />
      {tag}
      {study.leadingMm != null ? (
        <span className="oximg-pic-lead">lead {mmText(study.leadingMm)} mm</span>
      ) : study.endoMm != null ? (
        <span className="oximg-pic-lead">endo {mmText(study.endoMm)} mm</span>
      ) : null}

      <div className={"oximg-pic-bar" + (wlOpen ? " ox-hot" : "")}>
        <button type="button" className="oximg-pb" title="Zoom out"
          onClick={() => setZoom(view.current.z / 1.25)}>−</button>
        <span className="oximg-pb-pct" title="Back to fit" onClick={() => setZoom(1)}>{pct}%</span>
        <button type="button" className="oximg-pb" title="Zoom in"
          onClick={() => setZoom(view.current.z * 1.25)}>+</button>
        <button type="button" className="oximg-pb" title="Fit the whole image"
          onClick={() => setZoom(1)}>Fit</button>
        <button type="button" className="oximg-pb" title="One image pixel per screen pixel"
          onClick={() => {
            // The laid-out width already includes the current scale, so it
            // divides back out.
            const c = canvasRef.current;
            const shown = c ? c.getBoundingClientRect().width : 0;
            if (shown > 0) setZoom((c.width / shown) * view.current.z);
          }}>1:1</button>
        <span className="oximg-pb-sp" />
        <button type="button" className={"oximg-pb" + (wlOpen ? " ox-on" : "")}
          title="Contrast & brightness (window / level)"
          onClick={() => setWlOpen(!wlOpen)}>W/L</button>
        <button type="button" className="oximg-pb" title="Reset this image" onClick={reset}>⟲</button>
      </div>

      <div className={"oximg-wlpop" + (wlOpen ? " ox-on" : "")} onClick={(e) => e.stopPropagation()}>
        <div className="oximg-wlrow">
          W
          <input type="range" min="1" max="255" value={ww} aria-label="Window width (contrast)"
            onChange={(e) => setWw(Math.max(1, toNum(e.target.value) || WW_DEFAULT))} />
          <span>{ww}</span>
        </div>
        <div className="oximg-wlrow">
          L
          <input type="range" min="0" max="255" value={wl} aria-label="Window level (brightness)"
            onChange={(e) => setWl(toNum(e.target.value))} />
          <span>{wl}</span>
        </div>
        <button type="button" onClick={reset}>Reset contrast</button>
      </div>
    </div>
  );
}

/* ── LEFT TILE — one row per scan ────────────────────────────────────────── */

function StudyRow({ study, active, ticked, onToggle, onSelect }) {
  const badge = study.side === "R" || study.side === "L" ? study.side
    : study.kind === "uterus" ? "U" : "X";
  const badgeTitle = badge === "R" ? "Right ovary"
    : badge === "L" ? "Left ovary"
      : badge === "U" ? "Uterus — endometrial thickness"
        : "This file doesn't say which ovary was scanned";

  // A row with nothing to read says why, rather than leaving the reader to
  // work it out. A uterus view is measured in endometrium, not follicles, so
  // "no follicle sizes" would be the wrong complaint.
  const sub = study.diameters.length
    ? study.diameters.length + " follicle" + (study.diameters.length === 1 ? "" : "s") +
    (study.leadingMm != null ? " · lead " + mmText(study.leadingMm) + " mm" : "")
    : study.endoMm != null
      ? "Endometrium " + mmText(study.endoMm) + " mm" + (study.endoPattern ? " · " + study.endoPattern : "")
      : study.kind === "uterus"
        ? <span className="oximg-nosize">No endometrium reading</span>
        : <span className="oximg-nosize">No follicle sizes</span>;

  return (
    <div className={"oximg-study" + (active ? " ox-active" : "")} onClick={onSelect}>
      <input
        type="checkbox"
        className="oximg-cb-scan"
        checked={ticked}
        title={ticked ? "Untick — hides this scan on its day card" : "Tick — shows this scan on its day card"}
        aria-label={slotName(study) + " — show on the day card"}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
      />
      <div className="oximg-study-main">
        {/* The title is ellipsised, so nothing may follow it on this line. */}
        <div className="oximg-study-name" title={study.fileName}>
          <span className={"oximg-side ox-" + badge} title={badgeTitle}>{badge}</span>
          {studyRowTitle(study)}
        </div>
        {/* Chips live on the sub line, where they wrap instead of clipping. */}
        <div className="oximg-study-sub">
          <span className="oximg-study-when">{sub}</span>
          <span className="oximg-origin ox-sp" title="Loaded from this cycle's SharePoint folder">SP</span>
        </div>
      </div>
    </div>
  );
}

function FileRow({ file, mkUrl }) {
  const ext = fileExt(file.name).toUpperCase();
  const meta = [];
  if (file.bytes) meta.push(fileSizeText(file.bytes));
  const when = apiFileDate(file.date);
  if (when) meta.push(when);
  const sp = hrefFor(apiFileSpUrl(file));
  // A .dcm has no viewer outside this page, so "Open" is only offered for the
  // types a browser can actually render.
  const canOpen = file.base64 && !["dicom", "office", "archive", "other"].includes(file.kind);
  // Images preview in place — for a photo or a report snapshot the thumbnail
  // is the fastest way to know which file this is.
  const thumb = file.kind === "image" && file.base64 ? mkUrl(file) : "";

  const download = () => {
    const url = mkUrl(file);
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className={"oximg-file" + (file.inCycle ? "" : " ox-outside")}>
      <div className="oximg-file-ico">
        {thumb ? <img src={thumb} alt={file.name} loading="lazy" /> : (file.icon || "📎")}
      </div>
      <div className="oximg-file-main">
        <div className="oximg-file-name" title={file.name}>{file.name}</div>
        <div className="oximg-file-sub">
          <span className="oximg-file-kind">{ext || "FILE"}</span>
          {meta.length ? <span className="oximg-file-meta">{meta.join(" · ")}</span> : null}
          {file.parseError
            ? <span className="oximg-file-warn" title={file.parseError}>Not readable as DICOM</span>
            : null}
          {!file.base64 ? <span className="oximg-file-warn">No content returned</span> : null}
        </div>
        <div className="oximg-file-acts">
          {canOpen ? (
            <button type="button" className="oximg-file-btn" title="Open this file in a new tab"
              onClick={() => { const u = mkUrl(file); if (u) window.open(u, "_blank", "noopener"); }}>
              Open
            </button>
          ) : null}
          {file.base64 ? (
            <button type="button" className="oximg-file-btn" title="Download this file" onClick={download}>
              Save
            </button>
          ) : null}
          {sp ? (
            <a className="oximg-file-btn" href={sp} target="_blank" rel="noopener noreferrer"
              title="Open this file where it lives in SharePoint">SharePoint</a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ── CENTRE TILE — one card per scan day ─────────────────────────────────── */

function DayCard({ group, focused, shown, sideFilter, selectedUid, onFocus, onSelect }) {
  const items = group.items.slice().sort(
    (a, b) => (SLOT_ORDER[slotKey(a)] || 9) - (SLOT_ORDER[slotKey(b)] || 9)
  );
  const visible = items.filter(
    (s) => shown[s.uid] && (sideFilter === "all" || slotKey(s) === sideFilter)
  );
  const bySlot = (k) => items.filter((s) => slotKey(s) === k)[0];
  const R = bySlot("R"), L = bySlot("L"), U = bySlot("U");
  const sideLabel = (SIDE_FILTERS.find((f) => f.key === sideFilter) || { label: "All" }).label;

  return (
    <div className={"oximg-daycard" + (focused ? " ox-on" : "")}>
      <div className="oximg-dch" onClick={onFocus}>
        <span className="oximg-dch-day">{group.day || "Day not recorded"}</span>
        <span className="oximg-dch-date">{group.date || "no date"}</span>
        <span className="oximg-dch-sp" />
        {L && L.diameters.length ? (
          <span className="oximg-dch-stat">
            <span className="ox-cL">L</span> <b>{L.diameters.length}</b>
            {L.leadingMm != null ? "·" + mmText(L.leadingMm) : ""}
          </span>
        ) : null}
        {R && R.diameters.length ? (
          <span className="oximg-dch-stat">
            <span className="ox-cR">R</span> <b>{R.diameters.length}</b>
            {R.leadingMm != null ? "·" + mmText(R.leadingMm) : ""}
          </span>
        ) : null}
        {U && U.endoMm != null ? (
          <span className="oximg-dch-stat"><span className="ox-cU">U</span> endo <b>{mmText(U.endoMm)}</b></span>
        ) : null}
      </div>

      {visible.length ? (
        /* How tall the pictures on this day are is decided by how many there
           are. Three or more share the row, so each keeps the standard height;
           two get half again; a lone scan gets double. */
        <div className={"oximg-dcb" + (visible.length === 1 ? " ox-n1" : visible.length === 2 ? " ox-n2" : "")}>
          {visible.map((s) => (
            <div className="oximg-slot" key={s.uid}>
              <DicomPic study={s} selected={s.uid === selectedUid} onSelect={() => onSelect(s)} />
            </div>
          ))}
        </div>
      ) : (
        <div className="oximg-dc-none">
          {sideFilter === "all"
            ? <>Nothing ticked for this day.<br />Tick a scan in <b>Studies</b> to bring it back.</>
            : <>No {sideLabel.toLowerCase()} scan on this day.</>}
        </div>
      )}
    </div>
  );
}

/* ── RIGHT TILE — the numbers read off the file ──────────────────────────── */

function MeasBlock({ study, ticked, focused, onSelect }) {
  const slot = slotKey(study);
  const head = (
    <div className="oximg-mb-h">
      <span className={"oximg-pip ox-" + slot} />
      {slotName(study)}
      <span className="ox-sp" />
      <small>{ticked ? "shown" : "unticked"}</small>
    </div>
  );

  let body;
  if (slot === "U") {
    body = study.endoMm == null ? (
      <div className="oximg-empty" style={{ padding: "8px 2px" }}>
        No endometrial thickness in this file (checked the private tags,
        ImageComments and the series description).
      </div>
    ) : (
      <>
        <div className="oximg-tiles" style={{ gridTemplateColumns: "repeat(2,1fr)" }}>
          <div className="oximg-tile">
            <div className="ox-v" style={{ color: "var(--oximg-purple)" }}>{mmText(study.endoMm)}</div>
            <div className="ox-k">Endometrium mm</div>
          </div>
          <div className="oximg-tile">
            <div className="ox-v" style={{ fontSize: "12.5px", lineHeight: 1.35, padding: "3px 0" }}>
              {study.endoPattern || "—"}
            </div>
            <div className="ox-k">Pattern</div>
          </div>
        </div>
        <div className="oximg-src">Read from: {sourceText(study)}</div>
      </>
    );
  } else if (!study.diameters.length) {
    body = (
      <div className="oximg-empty" style={{ padding: "8px 2px" }}>
        No follicle sizes in this file (checked the private tags and ImageComments).
      </div>
    );
  } else {
    const sorted = study.diameters.slice().sort((a, b) => b - a);
    const leading = study.leadingMm != null ? study.leadingMm : sorted[0];
    const mean = study.meanMm != null ? study.meanMm : sumOf(sorted) / sorted.length;
    const over10 = sorted.filter((mm) => mm >= 10).length;
    // Only the single biggest chip is flagged; with two follicles at the same
    // size, highlighting both would misread as two leading follicles.
    let leadDone = false;
    body = (
      <>
        <div className="oximg-chips" style={{ marginTop: 0 }}>
          {sorted.map((mm, i) => {
            const isLead = !leadDone && leading != null && Math.abs(mm - leading) < 0.05;
            if (isLead) leadDone = true;
            return (
              <span key={i} className={"oximg-chip" + (isLead ? " ox-lead" : "")}
                title={isLead ? "Leading follicle" : undefined}>
                {mmText(mm)}<span className="ox-u">mm</span>
              </span>
            );
          })}
        </div>
        <div className="oximg-tiles">
          <div className="oximg-tile"><div className="ox-v">{sorted.length}</div><div className="ox-k">Count</div></div>
          <div className="oximg-tile ox-lead">
            <div className="ox-v">{leading != null ? mmText(leading) : "—"}</div><div className="ox-k">Lead</div>
          </div>
          <div className="oximg-tile">
            <div className="ox-v">{Number.isFinite(mean) ? mmText(mean) : "—"}</div><div className="ox-k">Mean</div>
          </div>
          <div className="oximg-tile"><div className="ox-v">{over10}</div><div className="ox-k">≥10 mm</div></div>
        </div>
      </>
    );
  }

  /* Unticked IS dimmed here, unlike the studies list: in the list a fade
     wrongly read as "disabled", but here it means "not on the wall", which is
     exactly true. */
  return (
    <div
      className={"oximg-mblock ox-" + slot + (focused ? " ox-foc" : "") + (ticked ? "" : " ox-dim")}
      onClick={onSelect}
    >
      {head}
      {body}
    </div>
  );
}

/* ── One pull per cycle, shared by every mount ────────────────────────────
   This section gets remounted a lot — a tab switch, a parent that re-renders,
   React 18's StrictMode double-mount — and each mount would otherwise start
   its own folder walk. So the pull is keyed by patient + cycle and kept at
   MODULE level, not in a ref: a ref dies with the component, so it cannot stop
   a remount loop from hammering the API.

     · a pull already in flight for that key is JOINED, never duplicated
     · a pull that finished is served straight from memory — no network at all

   The cache holds decoded pixel buffers (megabytes per study), so it is a
   small LRU rather than an unbounded map. Reload passes force and skips it. */
const CYCLE_INFLIGHT = new Map();
const CYCLE_CACHE = new Map();
const CYCLE_CACHE_MAX = 3;

/* What the reader has set up on screen — which scans are ticked, which days
   are open, which scan is highlighted, what is typed in the search box — kept
   beside the cycle's data and keyed the same way.

   It is here rather than in state alone for the same reason the cache is: a
   host page is free to rebuild this section whenever it re-renders, and every
   rebuild would otherwise put the ticks back to their defaults. A click that
   quietly undoes itself is indistinguishable from a click that did nothing. */
const VIEW_STATE = new Map();

function revokeFileUrls(list) {
  (list || []).forEach((f) => {
    if (f && f.blobUrl) {
      try { URL.revokeObjectURL(f.blobUrl); } catch (e) { /* already gone */ }
      f.blobUrl = "";
    }
  });
}

function cacheGet(key) {
  const hit = CYCLE_CACHE.get(key);
  if (!hit) return null;
  CYCLE_CACHE.delete(key);
  CYCLE_CACHE.set(key, hit);              // LRU touch
  return hit;
}
function cacheDrop(key) {
  const hit = CYCLE_CACHE.get(key);
  if (!hit) return;
  revokeFileUrls(hit.files);
  CYCLE_CACHE.delete(key);
  // The ticks are held by study uid, so they mean nothing once the studies
  // they point at are gone.
  VIEW_STATE.delete(key);
}
function cachePut(key, value) {
  CYCLE_CACHE.delete(key);
  CYCLE_CACHE.set(key, value);
  while (CYCLE_CACHE.size > CYCLE_CACHE_MAX) cacheDrop(CYCLE_CACHE.keys().next().value);
}

// Everything held in memory for every cycle. Call it on sign-out, or when a
// patient's folders have changed under us.
export function clearImagingCache() {
  Array.from(CYCLE_CACHE.keys()).forEach(cacheDrop);
  CYCLE_INFLIGHT.clear();
}

/* The whole network + parse job, with no React in it — which is what lets one
   job be shared by however many mounts ask for the same cycle. */

/* THE NAMES TRIED. The file service finds a cycle's folder by NAME, and the
   name the portal holds is not always the folder's spelling:
     - SharePoint will not take "/", so "IVF/ICSI" is "IVF-ICSI" on disk;
     - a name linked as "IVF-ICSI 12-Sep-2026 - Jane Patient - John Partner"
       is, on SharePoint, "... - Jane Patient & John Partner" (the couple's
       "&" was lost on the way, leaving a second " - ").
   The exact name goes first. Then, for a name with exactly two " - ", the last
   one is tried as " & ", " and " and " + ". The same fix as the clinical hub's
   dicomApi.js. `TreatmentAltNames` (set by the cycle page) adds the name built
   from the cycle's type, start date and couple. */
export function folderNameCandidates(name, extra) {
  const out = [];
  const add = (n) => {
    const t = safe(n).replace(/\s+/g, " ").trim();
    if (t && !out.includes(t)) out.push(t);
  };
  const withCouple = (n) => {
    const parts = n.split(" - ");
    if (parts.length === 3) {
      [" & ", " and ", " + "].forEach((sep) => add(parts[0] + " - " + parts[1] + sep + parts[2]));
    }
  };
  const bases = [name].concat(extra || []).filter(Boolean);
  bases.forEach((b) => {
    add(b);
    withCouple(safe(b));
    const noSlash = safe(b).replace(/\s*\/\s*/g, "-");
    if (noSlash !== b) { add(noSlash); withCouple(noSlash); }
  });
  return out;
}

function altNames() {
  try { return JSON.parse(sessionStorage.getItem("TreatmentAltNames") || "[]"); } catch { return []; }
}

async function pullCycle({ apiBase, tokenKey, userGuid, treatmentName: askedName }, onStep) {
  const parser = await ensureDicomParser();

  // Ask under each candidate name until one answers with files in this cycle.
  let treatmentName = askedName;
  let content = null;
  let all = [];
  const tried = [];
  for (const candidate of folderNameCandidates(askedName, altNames())) {
    if (onStep) onStep(tried.length ? "Trying another spelling of the folder name\u2026" : "Loading DICOM files for this treatment cycle\u2026");
    let tree = null;
    try {
      tree = await fetchCycleTree({ apiBase, tokenKey, userGuid, treatmentName: candidate }, onStep);
    } catch (e) {
      // The first (exact) name's failure is the real error; later spellings
      // only add chances.
      if (!tried.length) throw e;
    }
    tried.push(candidate);
    const found = collectApiFiles(tree, "", []);
    markApiFileScope(found, candidate);
    if (!content) { content = tree; all = found; treatmentName = candidate; }
    if (found.some((f) => f.inCycle)) { content = tree; all = found; treatmentName = candidate; break; }
  }
  if (typeof window !== "undefined") window.__imagingNamesTried = tried;
  all.forEach((f) => {
    const t = fileType(f.name);
    f.kind = t.kind; f.mime = t.mime; f.icon = t.icon;
    f.bytes = b64Size(f.base64);
  });

  // Only this cycle's .dcm files become studies — see markApiFileScope.
  const dcmFiles = all.filter((f) => f.inCycle && f.base64 && isDicomName(f.name));
  const studies = [];
  const failed = [];
  dcmFiles.forEach((f) => {
    try {
      studies.push(parseBytes(parser, f.name, base64ToBytes(f.base64)));
      f.studyLoaded = true;
    } catch (e) {
      //console.error("[Imaging] parse failed for", f.name, e);
      // It still appears in the file list below, where it can be opened or
      // saved — only the measurements side of it is what's lost.
      f.parseError = (e && e.message) || "not a readable DICOM file";
      failed.push(f.name);
    }
  });
  studies.sort(studyOrder);                          // date ascending, R before L

  // Everything that isn't a scan we read: other file types, and anything from
  // a folder outside this cycle.
  const files = all.filter((f) => !f.studyLoaded);
  const otherN = files.filter((f) => !f.parseError).length;

  return {
    studies,
    files,
    folderUrl: findCycleFolderUrl(content, treatmentName) || "",
    statusMsg:
      (studies.length
        ? "Loaded " + studies.length + " DICOM file(s) from this cycle's SharePoint folder"
        : "No DICOM files in this cycle's SharePoint folder") +
      (otherN > 0 ? " · " + otherN + " other file(s) listed below" : "") +
      (failed.length
        ? " — " + failed.length + " couldn't be read as DICOM (" + failed.join(", ") + "), still listed below"
        : "") + "."
  };
}

/* ── The section ─────────────────────────────────────────────────────────── */

export default function DicomViewer({
  // userGuid,
  // treatmentName,
  patientLabel = "",
  apiBase = API_BASE_DEFAULT,
  tokenKey = TOKEN_KEY_DEFAULT,
  rowLabels = DEFAULT_ROW_LABELS,
  autoLoad = true,
  className = ""
}) {
  const navigate = useNavigate();
  const [studies, setStudies] = useState([]);
  const [files, setFiles] = useState([]);
  const [folderUrl, setFolderUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ msg: "", err: false });
  const [query, setQuery] = useState("");
  // Two levels of ticking, and they deliberately mean different things:
  //   · scan tick (`shown`, by uid)  — what a day card shows.
  //   · day tick  (`daysShown`, key) — which day cards are in the centre.
  const [shown, setShown] = useState({});
  const [daysShown, setDaysShown] = useState({});
  const [selectedUid, setSelectedUid] = useState("");
  const [sideFilter, setSideFilter] = useState("all");
  const [oneCol, setOneCol] = useState(true);
  const [binsHideEmpty, setBinsHideEmpty] = useState(true);
  const userGuid = localStorage.getItem("UserID");
  const treatmentName = sessionStorage.getItem("TreatmentName");
  // Bumped on every load so a cycle switch mid-flight can't have the older
  // response land on top of the newer one.
  const reqRef = useRef(0);
  // Which cycle the studies currently on screen came from. Set by hydrate();
  // read by the view-state save below so a click is never filed against a
  // cycle the reader has already left.
  const loadedKeyRef = useRef("");
  /* Is this mount still on screen? The pull is shared, so it can outlive the
     mount that started it — and writing state into a mount that has gone is
     both pointless and a warning in the console. */
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  /* A blob URL per file, made on first use and kept on the file record, so
     opening the same file twice doesn't decode it twice. The bytes stay in
     memory until the URL is revoked — and the records outlive this mount in
     the cache, so releasing clears the mark too and the next mount rebuilds
     whichever URLs it actually needs. */
  const madeUrlsRef = useRef([]);
  const releaseUrls = useCallback(() => {
    revokeFileUrls(madeUrlsRef.current);
    madeUrlsRef.current = [];
  }, []);

  const mkUrl = useCallback((f) => {
    if (!f || !f.base64) return "";
    if (f.blobUrl) return f.blobUrl;
    try {
      const blob = new Blob([base64ToBytes(f.base64)], { type: f.mime || "application/octet-stream" });
      f.blobUrl = URL.createObjectURL(blob);
      madeUrlsRef.current.push(f);
      return f.blobUrl;
    } catch (e) {
      //console.warn("[Imaging] couldn't build a blob for", f.name, e);
      return "";
    }
  }, []);

  /* Put a finished pull on screen. If the reader has already set this cycle
     up — ticks, open days, the highlighted scan — that view is restored; this
     is what a remount lands back on. Otherwise the defaults: a newly loaded
     scan starts ticked, and the centre opens on the LATEST round only, since a
     cycle can carry a dozen scan days and opening on all of them buries the
     one that matters. */
  const hydrate = useCallback((res, key) => {
    // Which cycle the studies on screen belong to. The save effect below reads
    // it rather than recomputing the key from the props: when the cycle
    // changes, the props move a render BEFORE the new studies arrive, and
    // saving then filed the OLD cycle's ticks under the NEW cycle's key. The
    // next load restored them, none of their uids matched, and the section
    // came up with every scan unticked and no image on the wall.
    loadedKeyRef.current = key || "";

    setStudies(res.studies);
    setFiles(res.files);
    setFolderUrl(res.folderUrl);
    setStatus({ msg: res.statusMsg, err: false });

    /* A remembered view is only worth restoring if it is about THESE studies.
       Either it ticks one of them, or it highlights one — the second test is
       what lets a deliberate "Untick all" survive without letting a stale
       entry blank the section. */
    const saved = key ? VIEW_STATE.get(key) : null;
    const belongs = saved && res.studies.some(
      (s) => saved.shown[s.uid] || s.uid === saved.selectedUid
    );
    if (saved && belongs) {
      setShown(saved.shown);
      setDaysShown(saved.daysShown);
      setSelectedUid(saved.selectedUid);
      setSideFilter(saved.sideFilter);
      setOneCol(saved.oneCol);
      setQuery(saved.query);
      setBinsHideEmpty(saved.binsHideEmpty);
      return;
    }

    const nextShown = {};
    res.studies.forEach((s) => { nextShown[s.uid] = 1; });
    const gs = groupStudies(res.studies);
    const last = gs[gs.length - 1];
    setShown(nextShown);
    setDaysShown(last ? { [last.key]: 1 } : {});
    setSelectedUid(last && last.items[0] ? last.items[0].uid : "");
  }, []);

  // `force` is what the Reload button passes: read the folder again even
  // though this cycle is already in memory.
  const load = useCallback(async (force) => {
    if (!userGuid || !treatmentName) {
      setStudies([]); setFiles([]); setFolderUrl("");
      setStatus({
        msg: "This cycle has no " + (userGuid ? "name" : "patient") +
          " on the record, so its SharePoint folder can't be looked up.",
        err: true
      });
      return;
    }

    const key = apiBase + "|" + userGuid + "|" + treatmentName;
    const myReq = ++reqRef.current;
    // True when the cycle moved on — or this mount went away — while we were
    // awaiting: the result belongs to something the user is no longer looking
    // at, so it must be dropped rather than painted.
    const stale = () => reqRef.current !== myReq || !aliveRef.current;

    if (force === true) {
      cacheDrop(key);
    } else {
      // Already read this cycle: no network at all. This is what makes a
      // remount — or a parent stuck re-rendering — cost nothing.
      const hit = cacheGet(key);
      if (hit) { hydrate(hit, key); return; }
    }

    let job = CYCLE_INFLIGHT.get(key);
    if (!job) {
      job = pullCycle(
        { apiBase, tokenKey, userGuid, treatmentName },
        (m) => { if (!stale()) setStatus({ msg: m, err: false }); }
      ).then((res) => { cachePut(key, res); return res; });
      // Released however it ends, so a failed pull can be tried again.
      job.catch(() => {}).then(() => {
        if (CYCLE_INFLIGHT.get(key) === job) CYCLE_INFLIGHT.delete(key);
      });
      CYCLE_INFLIGHT.set(key, job);
    }

    setLoading(true);
    try {
      const res = await job;                  // a pull already running is joined
      if (stale()) return;
      hydrate(res, key);
    } catch (err) {
     // console.error("[Imaging] load failed:", err);
      if (stale()) return;
      // fetch() rejects with a bare TypeError for both a dead network and a
      // blocked cross-origin call, and the two need different people to fix
      // them — so say which is likelier rather than passing "Failed to fetch"
      // straight through.
      const msg = err && err.name === "TypeError"
        ? "the file API couldn't be reached — it may be down, or not allowing requests from this site (CORS)"
        : (err && err.message) || "unknown error";
      setStatus({ msg: "Couldn't load this cycle's imaging: " + msg, err: true });
    } finally {
      if (!stale()) setLoading(false);
    }
  }, [apiBase, tokenKey, userGuid, treatmentName, hydrate]);

  useEffect(() => { if (autoLoad) load(); }, [autoLoad, load]);
  useEffect(() => releaseUrls, [releaseUrls]);

  /* Every change to the view is written back beside its cycle, so the next
     mount can pick it up. It runs AFTER the render that showed the change, so
     a host page that rebuilds this section on the very next tick still finds
     the click that caused it.

     Filed under the key the studies on screen actually came from — NOT one
     rebuilt from the props. The props change a render before the new cycle's
     studies do, and that gap is long enough to file one cycle's ticks under
     another cycle's name. */
  useEffect(() => {
    const key = loadedKeyRef.current;
    if (!key || !studies.length) return;
    VIEW_STATE.set(key, {
      shown, daysShown, selectedUid, sideFilter, oneCol, query, binsHideEmpty
    });
  }, [studies, shown, daysShown, selectedUid, sideFilter, oneCol, query, binsHideEmpty]);

  /* ── Derived ───────────────────────────────────────────────────────── */

  const groups = useMemo(() => groupStudies(studies), [studies]);
  const terms = useMemo(() => searchTerms(query), [query]);

  // Groups are built from every study and then thinned, so a day heading only
  // survives while it still has a row under it.
  const listGroups = useMemo(() => {
    if (!terms.length) return groups;
    return groups
      .map((g) => ({ ...g, items: g.items.filter((s) => matchesTerms(studyHaystack(s), terms)) }))
      .filter((g) => g.items.length);
  }, [groups, terms]);

  const shownCount = listGroups.reduce((n, g) => n + g.items.length, 0);

  // The day whose measurements the right tile describes. Kept among the days
  // on screen, so those numbers never describe a card the user can't see.
  const focusGroup = useMemo(() => {
    if (!groups.length) return null;
    const sel = studies.find((s) => s.uid === selectedUid);
    if (sel) {
      const k = groupKey(sel);
      const hit = groups.find((g) => g.key === k);
      if (hit && daysShown[hit.key]) return hit;
    }
    const on = groups.filter((g) => daysShown[g.key]);
    return on[0] || groups[groups.length - 1];
  }, [groups, studies, selectedUid, daysShown]);

  const shownGroups = useMemo(() => {
    let list = groups.filter((g) => daysShown[g.key]);
    // Following one side across the round: a day with no scan of that side has
    // nothing to contribute, so it is left out rather than shown as an empty
    // card between the ones that do.
    if (sideFilter !== "all") {
      list = list.filter((g) => g.items.some((s) => shown[s.uid] && slotKey(s) === sideFilter));
    }
    return list;
  }, [groups, daysShown, sideFilter, shown]);

  const otherFiles = useMemo(() => {
    if (!terms.length) return files;
    return files.filter((f) => matchesTerms(fileHaystack(f), terms));
  }, [files, terms]);

  const fileGroups = useMemo(() => {
    // Grouped by the folder each file came from — with a recursive walk that
    // is the only thing telling a day subfolder's report from the one at the
    // top. This cycle's own folders come first.
    const out = [];
    otherFiles.forEach((f) => {
      const key = safe(f.folder) || "—";
      let g = out.find((x) => x.key === key);
      if (!g) { g = { key, inCycle: !!f.inCycle, items: [] }; out.push(g); }
      g.items.push(f);
    });
    out.sort((a, b) => (b.inCycle ? 1 : 0) - (a.inCycle ? 1 : 0));
    return out;
  }, [otherFiles]);

  const selected = studies.find((s) => s.uid === selectedUid) || null;

  /* ── Actions ───────────────────────────────────────────────────────── */

  // Move the selection onto a day, preferring a slot the user is already
  // looking at over an arbitrary first row.
  const focusDay = useCallback((group) => {
    if (!group || !group.items.length) return;
    const cur = studies.find((s) => s.uid === selectedUid);
    const want = cur ? slotKey(cur) : null;
    const hit = want && group.items.find((s) => slotKey(s) === want);
    setSelectedUid((hit || group.items[0]).uid);
  }, [studies, selectedUid]);

  const toggleDay = useCallback((g) => {
    const on = !!daysShown[g.key];
    setDaysShown((prev) => {
      const next = { ...prev };
      if (next[g.key]) delete next[g.key];
      else next[g.key] = 1;
      return next;
    });
    // Turning a day ON also brings the measurements with it; turning it off
    // leaves the selection where it was.
    if (!on) focusDay(g);
  }, [daysShown, focusDay]);

  const toggleScan = useCallback((s) => {
    setShown((prev) => {
      const next = { ...prev };
      if (next[s.uid]) delete next[s.uid];
      else next[s.uid] = 1;
      return next;
    });
  }, []);

  const selectStudy = useCallback((s) => {
    setSelectedUid(s.uid);
    setShown((prev) => (prev[s.uid] ? prev : { ...prev, [s.uid]: 1 }));
    // Clicking a row brings its day into view rather than quietly focusing a
    // card that is not on screen.
    setDaysShown((prev) => (prev[groupKey(s)] ? prev : { ...prev, [groupKey(s)]: 1 }));
  }, []);

  const focusIndex = focusGroup ? groups.findIndex((g) => g.key === focusGroup.key) : -1;
  const daysOnCount = groups.filter((g) => daysShown[g.key]).length;
  const singleDay = daysOnCount === 1;

  const stepDay = (delta) => {
    const g = groups[focusIndex + delta];
    if (!g) return;
    // One day on screen -> the arrows swap it. Several -> they move which card
    // the measurements describe.
    setDaysShown((prev) => (singleDay ? { [g.key]: 1 } : { ...prev, [g.key]: 1 }));
    focusDay(g);
  };

  /* ── Size bins for the selected scan ───────────────────────────────── */

  const bins = useMemo(() => {
    if (!selected || !selected.diameters.length) return null;
    const counts = {}, approx = {};
    let anyApprox = false, anyEmpty = false;
    selected.diameters.slice().sort((a, b) => b - a).forEach((mm) => {
      const row = rowForMm(mm, rowLabels);
      if (!row) return;
      counts[row.label] = (counts[row.label] || 0) + 1;
      if (!row.exact) { approx[row.label] = true; anyApprox = true; }
    });
    const rows = [];
    rowLabels.forEach((lb) => {
      if (/^total$/i.test(safe(lb))) return;                // derived, not a bin
      const n = counts[lb] || 0;
      if (!n) { anyEmpty = true; if (binsHideEmpty) return; }
      rows.push({ label: lb, n, approx: !!approx[lb] });
    });
    return { rows, anyApprox, anyEmpty };
  }, [selected, rowLabels, binsHideEmpty]);

  const sideLabel = (SIDE_FILTERS.find((f) => f.key === sideFilter) || { label: "All" }).label;
  const focusItems = focusGroup
    ? focusGroup.items.slice().sort((a, b) => (SLOT_ORDER[slotKey(a)] || 9) - (SLOT_ORDER[slotKey(b)] || 9))
    : [];
  const focusTicked = focusItems.filter((s) => shown[s.uid]).length;

  /* ── Paint ─────────────────────────────────────────────────────────── */

  return (
    <div className={"oximg " + className}>
      {/* No <style> here on purpose — see ensureStyles() at the foot of this
          file. A stylesheet rendered inside the tree is torn out and put back
          on every remount, which the host page sees as a flash of unstyled
          content each time it re-renders this section. */}

      {/* Scope strip — whose cycle this is, and where its files live. */}
      <div className="oximg-scope">
        <span className="oximg-scope-txt">
          Imaging for{" "}
          <b>{patientLabel || "this patient"}</b>
          {" · "}
          <b className="oximg-scope-cycle">{treatmentName || "no cycle selected"}</b>
        </span>
        <span className="oximg-scope-sp" />

        {/* {folderUrl ? (
          <a className="oximg-btn" href={hrefFor(folderUrl)}
            target="_blank" rel="noopener noreferrer"
            title="Open this cycle's folder in SharePoint">📁 Folder</a>
        ) : null} */}

        <button type="button" className="oximg-btn ox-primary"
          onClick={() => load(true)} disabled={loading} title="Read this cycle's SharePoint folder again">
          {loading
            ? <><span className="oximg-spin" role="status" aria-hidden="true" />Loading…</>
            : "↻ Reload"}
        </button>

        {/* Back to the treatment list — the same round arrow, in the same
            place (the far right), as the treatment's Details page uses. */}
        <button type="button" className="oximg-back" onClick={() => navigate(-1)}
          title="Back" aria-label="Back">
          <i className="fa-solid fa-circle-arrow-left" aria-hidden="true"></i>
        </button>
      </div>

      {status.msg ? (
        <div className={"oximg-status" + (status.err ? " ox-err" : "")}>{status.msg}</div>
      ) : null}

      {/* Three tiles — studies · day cards · measurements. */}
      <div className="oximg-body">

        {/* ── LEFT ─────────────────────────────────────────────────── */}
        <div className="oximg-tile oximg-tile-l">
          <div className="oximg-col">
            <div className="oximg-panel-title-row">
              <div className="oximg-panel-title">Studies</div>
            </div>

            {studies.length || files.length ? (
              <div className="oximg-search-wrap">
                <input
                  type="search"
                  className="oximg-search"
                  placeholder="🔍 Search scans and files…"
                  aria-label="Search this cycle's scans and files"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query ? (
                  <button type="button" className="oximg-search-clear"
                    title="Clear the search" aria-label="Clear the search"
                    onClick={() => setQuery("")}>&times;</button>
                ) : null}
              </div>
            ) : null}

            {/* Two tick shapes mean two different things in this list, so the
                legend draws the controls themselves. */}
            {studies.length ? (
              <>
                <div className="oximg-st-help">
                  <div><span className="oximg-k oximg-k-day" /><span><b>Day</b> — show this day in the centre</span></div>
                  <div><span className="oximg-k oximg-k-scan" /><span><b>Scan</b> — show this image on its day card</span></div>
                </div>
                <div className="oximg-bulk">
                  <button type="button" title="Tick every scan — both ovaries and the uterus on every day"
                    onClick={() => {
                      const next = {};
                      studies.forEach((s) => { next[s.uid] = 1; });
                      setShown(next);
                    }}>Tick all scans</button>
                  <button type="button" title="Untick every scan"
                    onClick={() => setShown({})}>Untick all</button>
                </div>
              </>
            ) : null}

            {!studies.length ? (
              <div className="oximg-empty">
                {loading ? "Loading this cycle's studies…" : "No studies for this cycle yet."}
              </div>
            ) : (
              <>
                <div className="oximg-study-summary">
                  <span><b>{listGroups.length}</b> scan day{listGroups.length === 1 ? "" : "s"}</span>
                  <span>· <b>{shownCount}</b> file{shownCount === 1 ? "" : "s"}</span>
                </div>
                {/* Say what the search is holding back, so a thinned list is
                    never mistaken for all this cycle has. */}
                {terms.length && shownCount < studies.length ? (
                  <div className="oximg-search-note">
                    Showing <b>{shownCount}</b> of <b>{studies.length}</b> scans.
                  </div>
                ) : null}

                {!listGroups.length ? (
                  <div className="oximg-empty">No scan matches “{query}”.</div>
                ) : (
                  <div className="oximg-study-scroll">
                    {listGroups.map((g) => {
                      const nOn = g.items.filter((s) => shown[s.uid]).length;
                      const on = !!daysShown[g.key];
                      return (
                        <div key={g.key} className={"oximg-daygrp" + (focusGroup && focusGroup.key === g.key ? " ox-on" : "")}>
                          {/* ONE action wherever the row is clicked, the tick
                              included — two rules on one row gave no way to
                              tell which you were about to get. */}
                          <div className="oximg-dayhead" onClick={() => toggleDay(g)}
                            title={(on ? "Hide" : "Show") + " " + (g.day || g.date || "this day") +
                              " in the centre  ·  " + nOn + " of " + g.items.length + " scans ticked"}>
                            <input type="checkbox" className="oximg-cb-day" checked={on} readOnly
                              aria-label={"Show " + (g.day || g.date || "this day") + " in the centre"} />
                            <span className="oximg-dayhead-main">
                              <span className="oximg-dayhead-day">{g.day || "Day not recorded"}</span>
                              <span className="oximg-dayhead-date">{g.date || "no date"}</span>
                            </span>
                            <span className="oximg-dayhead-sp" />
                            <span className="oximg-gn">{nOn}/{g.items.length}</span>
                          </div>
                          {/* Scans indented under their day on a shared rail,
                              so the list reads as parent-and-children. */}
                          <div className="oximg-kids">
                            {g.items.map((s) => (
                              <StudyRow
                                key={s.uid}
                                study={s}
                                active={s.uid === selectedUid}
                                ticked={!!shown[s.uid]}
                                onToggle={() => toggleScan(s)}
                                onSelect={() => selectStudy(s)}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Anything else the folder walk turned up: reports, photos, and
                scans that could not be read. */}
            {files.length ? (
              <div className="oximg-files-list">
                <div className="oximg-subh">
                  Other files · {otherFiles.length}
                  {otherFiles.length < files.length ? " of " + files.length : ""}
                  {(() => {
                    const b = otherFiles.reduce((n, f) => n + (f.bytes || 0), 0);
                    return b ? " · " + fileSizeText(b) : "";
                  })()}
                </div>
                {!otherFiles.length ? (
                  <div className="oximg-empty">No file matches “{query}”.</div>
                ) : (
                  <div className="oximg-files-scroll">
                    {fileGroups.map((g) => (
                      <div className="oximg-filegrp" key={g.key}>
                        <div className="oximg-filegrp-head">
                          <span className="oximg-filegrp-name" title={g.key}>📁 {g.key}</span>
                          {/* No badge for this cycle: the whole column is this
                              cycle, so only the exception is worth calling out. */}
                          {!g.inCycle ? (
                            <span className="oximg-filegrp-tag"
                              title="Not this cycle's folder — listed, but never read as a scan">
                              other folder
                            </span>
                          ) : null}
                        </div>
                        {g.items.map((f) => (
                          <FileRow key={(f.parentUrl || g.key) + "/" + f.name} file={f} mkUrl={mkUrl} />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* ── CENTRE ───────────────────────────────────────────────── */}
        <div className="oximg-tile oximg-tile-c">
          {groups.length ? (
            <div className="oximg-daystrip">
              <span className="oximg-ds-lbl">Days shown</span>
              <button type="button" className="oximg-ds-nav"
                disabled={focusIndex < 0 || focusIndex - 1 < 0}
                title={singleDay ? "Previous scan day" : "Focus the previous day"}
                onClick={() => stepDay(-1)}>‹</button>

              {groups.map((g) => {
                const on = !!daysShown[g.key];
                return (
                  <button
                    key={g.key}
                    type="button"
                    className={"oximg-ds-pill" + (on ? " ox-on" : "") +
                      (focusGroup && focusGroup.key === g.key && on ? " ox-focus" : "")}
                    title={(g.day || "Day") + " · " + (g.date || "no date") +
                      "\nClick to " + (on ? "remove from" : "add to") +
                      " the centre · double-click to show only this day"}
                    onClick={() => toggleDay(g)}
                    onDoubleClick={() => { setDaysShown({ [g.key]: 1 }); focusDay(g); }}
                  >
                    <span className="oximg-ds-box" />
                    {g.day || "Day"} <small>{g.date || ""}</small>
                  </button>
                );
              })}

              <button type="button" className="oximg-ds-nav"
                disabled={focusIndex < 0 || focusIndex + 1 >= groups.length}
                title={singleDay ? "Next scan day" : "Focus the next day"}
                onClick={() => stepDay(1)}>›</button>

              <span style={{ flex: 1 }} />
              <span className="oximg-ds-count">{daysOnCount} of {groups.length} shown</span>

              <button type="button"
                className={"oximg-ds-btn" + (daysOnCount === groups.length ? " ox-on" : "")}
                title="Show every scan day at once"
                onClick={() => {
                  const next = {};
                  groups.forEach((g) => { next[g.key] = 1; });
                  setDaysShown(next);
                }}>▦ Show all</button>

              {/* Named for what it actually does: it has always landed on one
                  day, never on none. */}
              <button type="button" className={"oximg-ds-btn" + (singleDay ? " ox-on" : "")}
                disabled={singleDay}
                title="Back to a single day card — the latest round"
                onClick={() => {
                  const g = groups[groups.length - 1];
                  setDaysShown({ [g.key]: 1 });
                  focusDay(g);
                }}>▤ Show only 1</button>

              <button type="button" className={"oximg-ds-btn" + (oneCol ? " ox-on" : "")}
                title={oneCol
                  ? "One day per row — biggest possible images. Click to fit days across the width."
                  : "Days fitted across the width. Click for one per row."}
                onClick={() => setOneCol(!oneCol)}>
                {oneCol ? "▤ One per row" : "▥ Fit across"}
              </button>

              {/* Follow one side across the whole round. Picking a side also
                  opens every day and drops the one-per-row layout, because the
                  point of asking for "just the left ovary" is to see it grow
                  day by day. */}
              <div className="oximg-ds-seg">
                <span className="oximg-ds-lbl">View</span>
                {SIDE_FILTERS.map((f) => (
                  <button key={f.key} type="button"
                    className={"oximg-ds-side" + (sideFilter === f.key ? " ox-on" : "")}
                    title={f.key === "all"
                      ? "Show every scan of each day"
                      : "Show only the " + f.label.toLowerCase() + " across every scan day"}
                    onClick={() => {
                      if (sideFilter === f.key) return;
                      setSideFilter(f.key);
                      if (f.key === "all") {
                        setOneCol(true);                       // back to the reading layout
                      } else {
                        const next = {};
                        groups.forEach((g) => { next[g.key] = 1; });
                        setDaysShown(next);
                        setOneCol(false);                      // days abreast, to compare
                      }
                    }}>
                    {f.key === "all" ? null : <span className={"oximg-pip ox-" + f.key} />}
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {shownGroups.length ? (
            <div className={"oximg-daycards" + (oneCol ? " ox-one-col" : "")}>
              {shownGroups.map((g) => (
                <DayCard
                  key={g.key}
                  group={g}
                  focused={!!(focusGroup && focusGroup.key === g.key)}
                  shown={shown}
                  sideFilter={sideFilter}
                  selectedUid={selectedUid}
                  onFocus={() => focusDay(g)}
                  onSelect={(s) => setSelectedUid(s.uid)}
                />
              ))}
            </div>
          ) : (
            <div className="oximg-daycards-none">
              {!studies.length
                ? (loading ? "Loading this cycle's scans…" : "No studies for this cycle yet.")
                : sideFilter !== "all"
                  ? <>No <b>{sideLabel.toLowerCase()}</b> scan on any day shown.<br />
                    Pick <b>All</b> above, or show more days.</>
                  : <>No scan day to show.<br />Tick a day in <b>Studies</b>, or use <b>▦ Show all</b> above.</>}
            </div>
          )}
        </div>

        {/* ── RIGHT ────────────────────────────────────────────────── */}
        <div className="oximg-tile oximg-tile-r">
          <div className="oximg-col">
            <div className="oximg-panel-title">Measurements</div>

            {!focusGroup ? (
              <div className="oximg-empty">No studies for this cycle yet.</div>
            ) : (
              <>
                <div className="oximg-meas-when">
                  {[focusGroup.day, focusGroup.date].filter(Boolean).join("  ·  ")}
                  {"  ·  " + focusTicked + " of " + focusItems.length + " ticked"}
                  {shownGroups.length > 1 ? (
                    <><br /><span style={{ color: "var(--oximg-accent)", fontWeight: 600 }}>
                      These follow the highlighted card
                    </span> — click another day card to switch.</>
                  ) : null}
                </div>

                {focusItems.map((s) => (
                  <MeasBlock
                    key={s.uid}
                    study={s}
                    ticked={!!shown[s.uid]}
                    focused={s.uid === selectedUid}
                    onSelect={() => setSelectedUid(s.uid)}
                  />
                ))}

                {/* Size bins — which folliculogram row each measurement of the
                    SELECTED scan lands in. Only the rows that caught something
                    are listed by default. */}
                {selected && selected.kind === "uterus" && selected.endoMm != null ? (
                  <>
                    <div className="oximg-subh">Endometrium</div>
                    <div className="oximg-src">
                      This scan reads <b>{mmText(selected.endoMm)} mm</b>
                      {selected.date ? " for " + selected.date : ""}
                      {selected.endoPattern ? " · " + selected.endoPattern : ""}.
                    </div>
                    <div className="oximg-src">Read from: {sourceText(selected)}</div>
                  </>
                ) : bins ? (
                  <>
                    <div className="oximg-subh-row">
                      <div className="oximg-subh">Size bins · {slotName(selected)}</div>
                      {bins.anyEmpty ? (
                        <button type="button"
                          className={"oximg-bins-toggle" + (binsHideEmpty ? " ox-active" : "")}
                          title={binsHideEmpty ? "Show every row of the scale" : "Hide the rows that caught nothing"}
                          onClick={() => setBinsHideEmpty(!binsHideEmpty)}>
                          <span aria-hidden="true">⊘</span> {binsHideEmpty ? "Show all" : "Hide empty"}
                        </button>
                      ) : null}
                    </div>
                    {bins.rows.length ? (
                      <table className="oximg-foll">
                        <tbody>
                          <tr><th>Bin</th><th>n</th></tr>
                          {bins.rows.map((r) => (
                            <tr key={r.label} className={r.n ? "ox-has" + (r.approx ? " ox-approx" : "") : ""}>
                              <td>{r.label}</td>
                              <td className="oximg-bin-n">{r.n || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="oximg-empty">None of these sizes fall into a row on this scale.</div>
                    )}
                    {bins.anyApprox ? (
                      <div className="oximg-src">
                        ≈ — this scale has no row for that exact size, so it is counted in the nearest one.
                      </div>
                    ) : null}
                    <div className="oximg-src">Read from: {sourceText(selected)}</div>
                  </>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────────────────
   Scoped under .oximg so nothing here can reach the rest of the app. The
   palette is defined as variables on the root, with a dark set for both
   Bootstrap 5.3's data-bs-theme and the OS preference. */
const CSS = `
.oximg {
  --oximg-bg:#f6f8fa; --oximg-bg2:#ffffff; --oximg-bg3:#f0f2f4; --oximg-bg4:#e8eaec;
  --oximg-border:#d0d7de; --oximg-border2:#afb8c1;
  --oximg-text:#1f2328; --oximg-text2:#57606a; --oximg-text3:#8c959f;
  --oximg-accent:#0969da; --oximg-green:#1a7f37; --oximg-yellow:#9a6700;
  --oximg-red:#d1242f; --oximg-purple:#8250df;
  --oximg-right:#d1242f; --oximg-left:#0969da;
  color: var(--oximg-text);
  font-size: 13px;
}
[data-bs-theme="dark"] .oximg {
  --oximg-bg:#0d1117; --oximg-bg2:#161b22; --oximg-bg3:#21262d; --oximg-bg4:#2d333b;
  --oximg-border:#30363d; --oximg-border2:#444c56;
  --oximg-text:#e6edf3; --oximg-text2:#8b949e; --oximg-text3:#6e7681;
  --oximg-accent:#58a6ff; --oximg-green:#3fb950; --oximg-yellow:#d29922;
  --oximg-red:#f85149; --oximg-purple:#bc8cff;
  --oximg-right:#f85149; --oximg-left:#58a6ff;
}

/* Every control this section needs, drawn here rather than borrowed from
   Bootstrap. The section then looks the same whether or not Bootstrap's CSS
   happens to be loaded on the route it is dropped into — and its own layout
   can never be re-flowed by a utility class from somewhere else. */
.oximg *, .oximg *::before, .oximg *::after { box-sizing: border-box; }
.oximg-btn {
  display:inline-flex; align-items:center; gap:5px; white-space:nowrap;
  border:1px solid var(--oximg-border); border-radius:7px; background:var(--oximg-bg2);
  color:var(--oximg-text2); font:inherit; font-size:11.5px; font-weight:600;
  padding:4px 10px; cursor:pointer; text-decoration:none; line-height:1.5;
}
.oximg-btn:hover:not(:disabled) { background:var(--oximg-bg4); color:var(--oximg-text); border-color:var(--oximg-border2); }
.oximg-btn.ox-primary { border-color:var(--oximg-accent); color:var(--oximg-accent); }
.oximg-btn.ox-primary:hover:not(:disabled) { background:var(--oximg-accent); color:#fff; }
.oximg-btn:disabled { opacity:.55; cursor:default; }
.oximg-spin {
  width:12px; height:12px; border-radius:50%; flex:0 0 auto;
  border:2px solid currentColor; border-right-color:transparent;
  animation:oximg-spin .7s linear infinite; display:inline-block;
}
@keyframes oximg-spin { to { transform:rotate(360deg); } }

/* Three tiles — studies · day cards · measurements. The widths are the ones
   the source page settles on; below 1200px they stack, because a 236px column
   of scan rows beside a scan you cannot read helps nobody. */
.oximg-body { display:grid; gap:12px; grid-template-columns:236px minmax(0,1fr) 330px; align-items:start; }
.oximg-tile { min-width:0; }
@media (max-width: 1200px) {
  .oximg-body { grid-template-columns:1fr; }
}

/* Scope strip + status */
.oximg-scope {
  display:flex; align-items:center; flex-wrap:wrap; gap:8px;
  background:var(--oximg-bg2); border:1px solid var(--oximg-border); border-radius:10px;
  padding:8px 12px; margin-bottom:10px; font-size:12px; color:var(--oximg-text2);
}
.oximg-scope-cycle { color:var(--oximg-text); }
.oximg-scope-sp { flex:1; }
.oximg-back {
  display:inline-flex; align-items:center; justify-content:center;
  padding:0 2px; border:none; background:none; cursor:pointer;
  font-size:18px; line-height:1; color:var(--oximg-text);
}
.oximg-back:hover { color:var(--oximg-accent); }
.oximg-back:focus-visible { outline:2px solid var(--oximg-accent); outline-offset:2px; border-radius:50%; }
.oximg-status { font-size:12px; color:var(--oximg-accent); margin:0 0 10px; line-height:1.5; }
.oximg-status.ox-err { color:var(--oximg-red); }

/* Column shells */
.oximg-col {
  background:var(--oximg-bg2); border:1px solid var(--oximg-border);
  border-radius:8px; padding:12px; min-width:0;
}
.oximg-panel-title {
  font-size:11px; font-weight:700; color:var(--oximg-text3);
  letter-spacing:.07em; text-transform:uppercase; margin-bottom:10px;
}
.oximg-panel-title-row { display:flex; align-items:center; justify-content:space-between; gap:8px; }
.oximg-panel-title-row .oximg-panel-title { margin-bottom:10px; }
.oximg-empty { color:var(--oximg-text3); font-size:12.5px; padding:18px 2px; text-align:center; line-height:1.55; }

/* Search */
.oximg-search-wrap { position:relative; margin-bottom:9px; min-width:0; }
.oximg-search {
  width:100%; padding:5px 26px 5px 10px; font:inherit; font-size:12px;
  color:var(--oximg-text); background:var(--oximg-bg);
  border:1px solid var(--oximg-border); border-radius:7px;
}
.oximg-search:focus { outline:none; border-color:var(--oximg-accent); box-shadow:0 0 0 3px rgba(9,105,218,.18); }
[data-bs-theme="dark"] .oximg-search:focus { box-shadow:0 0 0 3px rgba(88,166,255,.22); }
.oximg-search::placeholder { color:var(--oximg-text3); }
.oximg-search::-webkit-search-cancel-button { -webkit-appearance:none; appearance:none; }
.oximg-search-clear {
  position:absolute; right:4px; top:50%; transform:translateY(-50%);
  border:0; background:none; color:var(--oximg-text3); font-size:16px;
  line-height:1; width:20px; height:20px; border-radius:5px; cursor:pointer;
}
.oximg-search-clear:hover { color:var(--oximg-text); background:var(--oximg-bg4); }
.oximg-search-note { font-size:10.5px; color:var(--oximg-text3); margin:-4px 0 8px; }
.oximg-search-note b { color:var(--oximg-text2); font-weight:700; }

/* Legend + bulk ticks */
.oximg-st-help {
  font-size:10.5px; line-height:1.6; color:var(--oximg-text3); background:var(--oximg-bg);
  border:1px solid var(--oximg-border); border-radius:7px; padding:6px 8px; margin-bottom:8px;
}
.oximg-st-help b { color:var(--oximg-text2); font-weight:700; }
.oximg-st-help div { display:flex; align-items:center; gap:6px; }
.oximg-st-help div + div { margin-top:3px; }
.oximg-k { width:12px; height:12px; flex-shrink:0; border:1.5px solid var(--oximg-accent);
           background:var(--oximg-accent); position:relative; }
.oximg-k-day { border-radius:50%; }
.oximg-k-day::after { content:''; position:absolute; inset:2.5px; border-radius:50%; background:var(--oximg-bg2); }
.oximg-k-scan { border-radius:3px; }
.oximg-k-scan::after {
  content:'\\2713'; position:absolute; inset:0; color:#fff; font-size:9px; font-weight:900;
  display:flex; align-items:center; justify-content:center; line-height:1;
}
.oximg-bulk { display:flex; gap:5px; margin-bottom:8px; }
.oximg-bulk button {
  flex:1; border:1px solid var(--oximg-border); background:var(--oximg-bg3); color:var(--oximg-text2);
  border-radius:6px; padding:3px 6px; font-size:10.5px; font-weight:600; cursor:pointer;
  white-space:nowrap; overflow:hidden;
}
.oximg-bulk button:hover { background:var(--oximg-bg4); color:var(--oximg-text); }

/* Study list */
.oximg-study-summary {
  display:flex; align-items:center; flex-wrap:wrap; gap:4px 6px;
  font-size:11px; line-height:1.45; color:var(--oximg-text3);
  background:var(--oximg-bg3); border:1px solid var(--oximg-border); border-radius:8px;
  padding:6px 9px; margin-bottom:9px;
}
.oximg-study-summary b { color:var(--oximg-text); font-weight:700; }
.oximg-study-scroll { position:relative; max-height:520px; overflow-y:auto; margin:0 -3px; padding:0 3px; }
.oximg-daygrp { margin-bottom:10px; }
.oximg-daygrp:last-child { margin-bottom:0; }
/* The heading rides with its day while the list scrolls. The tint is LAYERED
   over an opaque base, not used as the background: this header is sticky, and
   a translucent fill lets the rows scrolling under it show through. */
.oximg-dayhead {
  display:flex; align-items:center; justify-content:space-between; gap:8px;
  padding:4px 9px; margin-bottom:5px; border-radius:6px; cursor:pointer;
  background:var(--oximg-bg3); border:1px solid var(--oximg-border);
  position:sticky; top:0; z-index:3;
}
.oximg-daygrp.ox-on .oximg-dayhead {
  background:linear-gradient(rgba(9,105,218,0.12), rgba(9,105,218,0.12)), var(--oximg-bg3);
  box-shadow:inset 0 0 0 1px rgba(9,105,218,0.32);
}
[data-bs-theme="dark"] .oximg-daygrp.ox-on .oximg-dayhead {
  background:linear-gradient(rgba(88,166,255,0.16), rgba(88,166,255,0.16)), var(--oximg-bg3);
}
.oximg-daygrp.ox-on .oximg-dayhead-day { color:var(--oximg-accent); }
.oximg-dayhead-main { display:flex; flex-direction:column; min-width:0; }
.oximg-dayhead-day {
  font-size:11px; font-weight:800; color:var(--oximg-text); text-transform:uppercase;
  letter-spacing:.05em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.oximg-dayhead-date {
  font-size:10.5px; color:var(--oximg-text3); white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis; margin-top:1px;
}
.oximg-dayhead-sp { flex:1; }
.oximg-gn {
  font-size:9px; font-weight:700; color:var(--oximg-text3); background:var(--oximg-bg2);
  border:1px solid var(--oximg-border); border-radius:9px; padding:0 5px;
  white-space:nowrap; flex-shrink:0;
}
/* The DAY control is a checkbox in every functional sense — several days can
   be on at once — but is drawn as a radio so it reads as a different kind of
   control from the square scan ticks nested under it. */
.oximg-cb-day {
  appearance:none; -webkit-appearance:none; margin:0;
  width:15px; height:15px; border-radius:50%; flex-shrink:0; position:relative;
  border:1.5px solid var(--oximg-border2); background:var(--oximg-bg2); cursor:pointer;
}
.oximg-cb-day:checked { border-color:var(--oximg-accent); background:var(--oximg-accent); }
.oximg-cb-day:checked::after { content:''; position:absolute; inset:3.5px; border-radius:50%; background:#fff; }
.oximg-cb-scan { width:14px; height:14px; accent-color:var(--oximg-accent); cursor:pointer; flex-shrink:0; }
.oximg-kids { margin:2px 0 0 6px; padding-left:8px; border-left:2px solid var(--oximg-border); }
.oximg-daygrp.ox-on .oximg-kids { border-left-color:var(--oximg-accent); }

.oximg-study {
  display:flex; gap:10px; padding:7px; margin-bottom:4px;
  border:1px solid transparent; border-radius:8px; cursor:pointer;
}
.oximg-study:hover { background:var(--oximg-bg3); }
.oximg-study.ox-active { background:rgba(9,105,218,0.1); border-color:var(--oximg-accent); }
[data-bs-theme="dark"] .oximg-study.ox-active { background:rgba(88,166,255,0.1); }
.oximg-study-main { min-width:0; flex:1; }
.oximg-study-name {
  font-size:12.5px; font-weight:600; color:var(--oximg-text);
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.oximg-study-sub {
  font-size:10.5px; color:var(--oximg-text3); margin-top:2px;
  display:flex; align-items:center; flex-wrap:wrap; gap:4px; row-gap:3px;
}
.oximg-study-when { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.oximg-study-sub .oximg-nosize { color:var(--oximg-yellow); font-weight:600; }
.oximg-origin {
  font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.04em;
  padding:1px 5px; border-radius:8px; background:var(--oximg-bg3); color:var(--oximg-text3); flex:0 0 auto;
}
.oximg-origin.ox-sp { background:rgba(26,155,161,0.14); color:#1A9BA1; }
.oximg-side { display:inline-block; font-size:9.5px; font-weight:800; padding:1px 6px; border-radius:10px; margin-right:4px; }
.oximg-side.ox-R { background:rgba(209,36,47,0.12); color:var(--oximg-right); }
.oximg-side.ox-L { background:rgba(9,105,218,0.12); color:var(--oximg-left); }
.oximg-side.ox-U { background:rgba(130,80,223,0.13); color:var(--oximg-purple); }
.oximg-side.ox-X { background:var(--oximg-bg4); color:var(--oximg-text3); }

/* Other files */
.oximg-files-list { margin-top:12px; }
.oximg-files-scroll { max-height:260px; overflow-y:auto; margin:0 -3px; padding:0 3px; }
.oximg-filegrp { margin-bottom:9px; }
.oximg-filegrp-head { display:flex; align-items:center; gap:6px; margin-bottom:4px; }
.oximg-filegrp-name {
  font-size:10.5px; font-weight:700; color:var(--oximg-text2); min-width:0;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.oximg-filegrp-tag {
  font-size:9px; font-weight:700; color:var(--oximg-yellow); background:rgba(154,103,0,0.12);
  border-radius:8px; padding:1px 6px; white-space:nowrap;
}
.oximg-file { display:flex; gap:9px; padding:6px 7px; border-radius:7px; }
.oximg-file:hover { background:var(--oximg-bg3); }
.oximg-file.ox-outside { opacity:.8; }
.oximg-file-ico {
  width:26px; height:26px; flex:0 0 auto; border-radius:6px; overflow:hidden;
  background:var(--oximg-bg3); display:flex; align-items:center; justify-content:center; font-size:14px;
}
.oximg-file-ico img { width:100%; height:100%; object-fit:cover; display:block; }
.oximg-file-main { min-width:0; }
.oximg-file-name {
  font-size:12px; font-weight:600; color:var(--oximg-text);
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.oximg-file-sub {
  display:flex; align-items:center; flex-wrap:wrap; gap:5px;
  font-size:10px; color:var(--oximg-text3); margin-top:2px;
}
.oximg-file-kind {
  font-size:9px; font-weight:700; letter-spacing:.04em; padding:1px 5px;
  border-radius:7px; background:var(--oximg-bg3); color:var(--oximg-text3);
}
.oximg-file-meta { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.oximg-file-warn { color:var(--oximg-yellow); font-weight:600; }
.oximg-file-acts { display:flex; align-items:center; flex-wrap:wrap; gap:4px; margin-top:5px; }
.oximg-file-btn {
  border:1px solid var(--oximg-border); background:var(--oximg-bg3); color:var(--oximg-text2);
  border-radius:6px; padding:2px 7px; font-size:10px; font-weight:600;
  cursor:pointer; text-decoration:none; line-height:1.5;
}
.oximg-file-btn:hover { background:var(--oximg-bg4); color:var(--oximg-text); border-color:var(--oximg-border2); }

/* Day strip */
.oximg-daystrip {
  display:flex; align-items:center; gap:5px; flex-wrap:wrap; background:var(--oximg-bg2);
  border:1px solid var(--oximg-border); border-radius:10px; padding:6px 8px; margin-bottom:10px;
}
.oximg-ds-lbl {
  font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.06em;
  color:var(--oximg-text3); white-space:nowrap;
}
.oximg-ds-nav {
  border:1px solid var(--oximg-border); background:var(--oximg-bg3); color:var(--oximg-text2);
  border-radius:6px; width:24px; height:24px; font-size:13px; cursor:pointer; flex-shrink:0;
}
.oximg-ds-nav:hover:not(:disabled) { background:var(--oximg-accent); border-color:var(--oximg-accent); color:#fff; }
.oximg-ds-nav:disabled { opacity:.35; cursor:default; }
.oximg-ds-pill {
  border:1px solid var(--oximg-border); background:var(--oximg-bg3); color:var(--oximg-text2);
  border-radius:7px; padding:4px 9px; font-size:11.5px; font-weight:600; cursor:pointer;
  display:flex; align-items:center; gap:6px; white-space:nowrap;
}
.oximg-ds-pill:hover { background:var(--oximg-bg4); color:var(--oximg-text); }
.oximg-ds-pill.ox-on { background:var(--oximg-accent); border-color:var(--oximg-accent); color:#fff; }
.oximg-ds-pill.ox-focus { box-shadow:0 0 0 2px var(--oximg-accent), 0 0 0 4px var(--oximg-bg); }
.oximg-ds-pill small { font-size:9.5px; opacity:.75; font-weight:500; }
.oximg-ds-box { width:12px; height:12px; border-radius:50%; border:1.5px solid var(--oximg-border2); flex-shrink:0; }
.oximg-ds-pill.ox-on .oximg-ds-box { background:#fff; border-color:#fff; }
.oximg-ds-count { font-size:10.5px; color:var(--oximg-text3); white-space:nowrap; font-variant-numeric:tabular-nums; }
.oximg-ds-btn {
  border:1px solid var(--oximg-border); background:var(--oximg-bg3); color:var(--oximg-text2);
  border-radius:7px; padding:4px 9px; font-size:11px; font-weight:600; cursor:pointer; white-space:nowrap;
}
.oximg-ds-btn:hover:not(:disabled) { background:var(--oximg-bg4); color:var(--oximg-text); }
.oximg-ds-btn.ox-on { background:var(--oximg-accent); border-color:var(--oximg-accent); color:#fff; }
.oximg-ds-btn:disabled { opacity:.4; cursor:default; }
.oximg-ds-seg {
  display:flex; align-items:center; gap:2px; padding:2px 2px 2px 8px;
  border:1px solid var(--oximg-border); border-radius:8px; background:var(--oximg-bg);
}
.oximg-ds-seg .oximg-ds-lbl { margin-right:4px; }
.oximg-ds-side {
  display:inline-flex; align-items:center; gap:5px; white-space:nowrap;
  border:0; background:transparent; color:var(--oximg-text2); cursor:pointer;
  font-size:11px; font-weight:600; padding:4px 9px; border-radius:6px;
}
.oximg-ds-side:hover { background:var(--oximg-bg3); color:var(--oximg-text); }
.oximg-ds-side.ox-on { background:var(--oximg-bg2); color:var(--oximg-text); box-shadow:0 1px 2px rgba(0,0,0,.12); }

/* Day cards. grid-auto-rows:max-content is load-bearing: without it a card
   taller than its row does not shrink — it overflows and paints over the card
   below, hiding that day's heading. */
.oximg-daycards {
  display:grid; gap:11px; min-width:0; grid-auto-rows:max-content;
  grid-template-columns:repeat(auto-fit, minmax(340px, 1fr));
}
.oximg-daycards.ox-one-col { grid-template-columns:1fr; }
.oximg-daycards-none {
  background:var(--oximg-bg2); border:1px dashed var(--oximg-border); border-radius:11px;
  padding:34px 16px; text-align:center; color:var(--oximg-text3); font-size:12.5px; line-height:1.65;
}
.oximg-daycard { background:var(--oximg-bg2); border:1px solid var(--oximg-border); border-radius:11px; min-width:0; }
.oximg-daycard.ox-on { border-color:var(--oximg-accent); box-shadow:0 0 0 1px var(--oximg-accent); }
/* NOT sticky. On the source page this column was its own scroller, so a
   sticky heading rode with its day; here the host page is the scroller, and
   top:0 would peel the heading off its card and float it over whatever the
   app has at the top of the viewport. */
.oximg-dch {
  display:flex; align-items:center; gap:9px; flex-wrap:wrap; padding:7px 11px;
  background:var(--oximg-bg3); border-bottom:1px solid var(--oximg-border); cursor:pointer;
  border-radius:10px 10px 0 0;
}
.oximg-daycard.ox-on .oximg-dch {
  background:linear-gradient(rgba(9,105,218,0.10), rgba(9,105,218,0.10)), var(--oximg-bg3);
}
[data-bs-theme="dark"] .oximg-daycard.ox-on .oximg-dch {
  background:linear-gradient(rgba(88,166,255,0.13), rgba(88,166,255,0.13)), var(--oximg-bg3);
}
.oximg-dch-day { font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.05em; }
.oximg-dch-date { font-size:11px; color:var(--oximg-text3); }
.oximg-dch-sp { flex:1; }
.oximg-dch-stat { font-size:11px; color:var(--oximg-text2); font-variant-numeric:tabular-nums; white-space:nowrap; }
.oximg-dch-stat .ox-cR { color:var(--oximg-right); }
.oximg-dch-stat .ox-cL { color:var(--oximg-left); }
.oximg-dch-stat .ox-cU { color:var(--oximg-purple); }
.oximg-dcb {
  display:grid; grid-template-columns:repeat(auto-fit, minmax(190px, 1fr));
  gap:1px; background:var(--oximg-border); grid-auto-rows:max-content; align-items:start;
  border-radius:0 0 10px 10px; overflow:hidden;
}
.oximg-dc-none {
  background:var(--oximg-bg2); padding:26px 14px; text-align:center;
  color:var(--oximg-text3); font-size:12px; line-height:1.6;
}
.oximg-slot { background:var(--oximg-bg2); display:flex; flex-direction:column; min-width:0; }

/* One image = one viewer. A REAL height, not one derived from the width:
   how many pictures that day is showing scales it. Three or more share the
   row and keep the base height; two get half again; a lone scan gets double. */
.oximg-dcb { --oximg-pic-h: clamp(240px, 34vh, 440px); }
.oximg-dcb.ox-n2 { --oximg-pic-h: clamp(360px, 51vh, 660px); }
.oximg-dcb.ox-n1 { --oximg-pic-h: clamp(480px, 68vh, 880px); }
.oximg-pic {
  position:relative; background:#05070a; overflow:hidden;
  height:var(--oximg-pic-h, clamp(240px, 34vh, 440px)); flex:0 0 auto;
  cursor:grab; touch-action:none;
}
.oximg-pic.ox-grabbing { cursor:grabbing; }
.oximg-pic.ox-sel { box-shadow:inset 0 0 0 2px var(--oximg-accent); }
.oximg-pic canvas {
  width:100%; height:100%; object-fit:contain; display:block;
  transform-origin:center center; will-change:transform;
}
.oximg-pic-tag {
  position:absolute; top:5px; left:6px; z-index:3; font-size:9.5px; font-weight:800;
  letter-spacing:.05em; padding:2px 7px; border-radius:5px; background:rgba(0,0,0,.6);
  color:#e6edf3; border-left:3px solid #8c959f; pointer-events:none;
}
.oximg-pic-tag.ox-R { border-left-color:#f85149; }
.oximg-pic-tag.ox-L { border-left-color:#58a6ff; }
.oximg-pic-tag.ox-U { border-left-color:#bc8cff; }
.oximg-pic-lead {
  position:absolute; bottom:32px; left:6px; z-index:3; font-size:9.5px; font-weight:700;
  padding:2px 7px; border-radius:5px; background:rgba(0,0,0,.55); color:#f2c744;
  pointer-events:none; transition:opacity .12s;
}
.oximg-pic:hover .oximg-pic-lead { opacity:0; }
.oximg-pic-none {
  color:#8b949e; font-size:11.5px; text-align:center; padding:16px; line-height:1.55;
  display:flex; align-items:center; justify-content:center; height:100%;
}
/* Always there but faint, solid on hover — discoverable without permanently
   covering the image. */
.oximg-pic-bar {
  position:absolute; left:0; right:0; bottom:0; z-index:4; display:flex;
  align-items:center; gap:2px; padding:3px 4px;
  background:linear-gradient(transparent, rgba(0,0,0,.78) 42%); opacity:.35; transition:opacity .13s;
}
.oximg-pic:hover .oximg-pic-bar, .oximg-pic-bar.ox-hot { opacity:1; }
.oximg-pb {
  border:0; background:rgba(255,255,255,.10); color:#e6edf3; border-radius:5px;
  min-width:20px; height:20px; padding:0 5px; font-size:11px; font-weight:700; cursor:pointer; line-height:1;
}
.oximg-pb:hover, .oximg-pb.ox-on { background:var(--oximg-accent); color:#fff; }
.oximg-pb-pct {
  color:#c9d1d9; font-size:9.5px; font-weight:700; min-width:36px; text-align:center;
  font-variant-numeric:tabular-nums; cursor:pointer;
}
.oximg-pb-sp { flex:1; }
.oximg-wlpop {
  position:absolute; right:4px; bottom:26px; z-index:6; display:none; flex-direction:column; gap:5px;
  background:rgba(13,17,23,.95); border:1px solid rgba(255,255,255,.16); border-radius:8px;
  padding:7px 9px; box-shadow:0 8px 24px rgba(0,0,0,.4); min-width:152px;
}
.oximg-wlpop.ox-on { display:flex; }
.oximg-wlrow { display:flex; align-items:center; gap:6px; color:#c9d1d9; font-size:10px; font-weight:700; }
.oximg-wlrow input[type=range] { flex:1; accent-color:#58a6ff; height:3px; }
.oximg-wlrow span { min-width:26px; text-align:right; font-variant-numeric:tabular-nums; font-weight:600; }
.oximg-wlpop button {
  border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.08);
  color:#e6edf3; border-radius:5px; padding:3px 0; font-size:10px; font-weight:600; cursor:pointer;
}
.oximg-wlpop button:hover { background:rgba(255,255,255,.18); }

/* Measurement blocks */
.oximg-pip { display:inline-block; width:8px; height:8px; border-radius:50%; flex-shrink:0; }
.oximg-pip.ox-R { background:var(--oximg-right); }
.oximg-pip.ox-L { background:var(--oximg-left); }
.oximg-pip.ox-U { background:var(--oximg-purple); }
.oximg-meas-when { font-size:11px; font-weight:500; color:var(--oximg-text3); margin-bottom:9px; line-height:1.5; }
.oximg-mblock {
  border:1px solid var(--oximg-border); border-radius:9px; padding:8px 9px;
  margin-bottom:8px; background:var(--oximg-bg); cursor:pointer;
}
.oximg-mblock.ox-R { border-left:3px solid var(--oximg-right); }
.oximg-mblock.ox-L { border-left:3px solid var(--oximg-left); }
.oximg-mblock.ox-U { border-left:3px solid var(--oximg-purple); }
.oximg-mblock.ox-foc { box-shadow:0 0 0 2px var(--oximg-accent); }
.oximg-mblock.ox-dim { opacity:.55; }
.oximg-mb-h {
  display:flex; align-items:center; gap:6px; font-size:12px; font-weight:700;
  margin-bottom:6px; flex-wrap:wrap;
}
.oximg-mb-h .ox-sp { flex:1; }
.oximg-mb-h small { font-weight:500; color:var(--oximg-text3); font-size:10.5px; }

/* One chip per follicle, largest first. The leading follicle is the one that
   drives the trigger decision, so it is called out. */
.oximg-chips { display:flex; flex-wrap:wrap; gap:5px; margin-top:9px; }
.oximg-chip {
  display:inline-flex; align-items:baseline; gap:3px; min-width:52px; justify-content:center;
  padding:4px 8px; border:1px solid var(--oximg-border); border-radius:7px; background:var(--oximg-bg2);
  font-size:12.5px; font-weight:700; color:var(--oximg-text); font-variant-numeric:tabular-nums;
}
.oximg-chip .ox-u { font-size:9.5px; font-weight:600; color:var(--oximg-text3); }
.oximg-chip.ox-lead { border-color:var(--oximg-yellow); background:rgba(154,103,0,0.10); color:var(--oximg-yellow); }
[data-bs-theme="dark"] .oximg-chip.ox-lead { background:rgba(210,153,34,0.14); }
.oximg-chip.ox-lead .ox-u { color:var(--oximg-yellow); }

/* Four figures, 2x2 — the shape they are quoted in on a round. */
.oximg-tiles { display:grid; grid-template-columns:1fr 1fr; gap:7px; margin-top:11px; }
.oximg-tile {
  border:1px solid var(--oximg-border); border-radius:9px; background:var(--oximg-bg2);
  padding:8px 6px; text-align:center;
}
.oximg-tile .ox-v {
  font-size:19px; font-weight:800; color:var(--oximg-text); line-height:1.15; font-variant-numeric:tabular-nums;
}
.oximg-tile .ox-k {
  font-size:9px; font-weight:700; color:var(--oximg-text3); text-transform:uppercase;
  letter-spacing:.06em; margin-top:2px;
}
.oximg-tile.ox-lead .ox-v { color:var(--oximg-yellow); }

/* Size bins */
table.oximg-foll {
  width:100%; font-size:12px; margin-top:10px;
  table-layout:fixed; border-collapse:separate; border-spacing:0;
}
table.oximg-foll th {
  color:var(--oximg-text3); font-weight:700; font-size:10px; text-transform:uppercase;
  letter-spacing:.05em; padding:0 0 7px; border-bottom:1px solid var(--oximg-border);
}
table.oximg-foll td { padding:7px 0; border-bottom:1px solid var(--oximg-border); color:var(--oximg-text); }
table.oximg-foll tr:last-child td { border-bottom:none; }
table.oximg-foll th:nth-child(1), table.oximg-foll td:nth-child(1) { text-align:left; padding-left:6px; }
table.oximg-foll th:nth-child(2), table.oximg-foll td:nth-child(2) { width:56px; text-align:right; padding-right:8px; }
table.oximg-foll td.oximg-bin-n { font-weight:700; }
table.oximg-foll tr.ox-has td { background:rgba(9,105,218,0.07); }
[data-bs-theme="dark"] table.oximg-foll tr.ox-has td { background:rgba(88,166,255,0.10); }
table.oximg-foll tr.ox-approx td:nth-child(1)::after { content:" ≈"; color:var(--oximg-yellow); font-weight:700; }

.oximg-src { font-size:10px; color:var(--oximg-text3); margin-top:8px; line-height:1.5; }
.oximg-subh {
  font-size:11px; font-weight:700; color:var(--oximg-text3);
  text-transform:uppercase; letter-spacing:.06em; margin:16px 0 6px;
}
.oximg-subh-row { display:flex; align-items:center; gap:8px; margin:16px 0 6px; }
.oximg-subh-row .oximg-subh { margin:0; }
.oximg-bins-toggle {
  margin-left:auto; padding:3px 9px; border:1px solid var(--oximg-border); border-radius:6px;
  background:var(--oximg-bg2); color:var(--oximg-text2); font-size:10.5px; font-weight:700;
  white-space:nowrap; cursor:pointer; display:inline-flex; align-items:center; gap:4px;
}
.oximg-bins-toggle:hover { background:var(--oximg-bg4); color:var(--oximg-text); }
.oximg-bins-toggle.ox-active { background:var(--oximg-accent); color:#fff; border-color:var(--oximg-accent); }
`;

/* ── One stylesheet, in <head>, put there once ────────────────────────────
   NOT rendered inside the section. A <style> element that lives in the React
   tree is removed and re-inserted every time the section is remounted, and the
   browser drops the rules in between — which the reader sees as the whole
   section flashing back to plain HTML on every interaction that makes the host
   page rebuild it. In <head> it is written once per page load and nothing the
   app does to this component can take it away. */
const OXIMG_STYLE_ID = "oximg-styles";
function ensureStyles() {
  if (typeof document === "undefined") return;             // server render
  const found = document.getElementById(OXIMG_STYLE_ID);
  if (found) {
    // Already written. Only rewritten when the rules actually differ, which is
    // what a hot reload during development needs and a no-op in production.
    if (found.textContent !== CSS) found.textContent = CSS;
    return;
  }
  const el = document.createElement("style");
  el.id = OXIMG_STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}
ensureStyles();
